import { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import {
  getSprints, getSprintBoard, getProjectBacklogBoard, updateSprintStatus,
  releaseSprint, createSprint, editSprint, deleteSprint, overrideSprintStatus,
  getBacklog, moveIssueToSprint,
} from '../api/sprintApi';
import { getProjects, getMyProjects, createProject } from '../api/projectApi';
import { updateIssueStatus, reassignIssue, updateEpicStatus, deleteIssue, updateIssue, getEpicsForProject } from '../api/issueApi';
import { uploadBRD, generateTasksFromBRD } from '../api/aiApi';
import { getVisibleUsers } from '../api/userApi';
import api from '../api/axios';
import toast from 'react-hot-toast';
import IssueForm from '../components/IssueForm';
import WorkItemCreateModal from '../components/WorkItemCreateModal';
import DailyNotebookModal from '../components/DailyNotebookModal';
import { issueFormModalPanelStyle } from '../constants/issueFormModal';
import TaskDetailModal from '../components/TaskDetailModal';
import BacklogDialog from '../components/BacklogDialog';
import { SprintReferenceLinks, SprintGoalLinkInput } from '../components/SprintReferenceLinks';
import {
  extractUrls,
  normalizeUrl,
  parseSprintGoal,
  composeSprintGoal,
} from '../utils/sprintGoalLinks';
import { getPriorityColor, getStatusColor } from '../theme/colors';
import { getCustomTeams } from '../api/configApi';
import {
  mergeTeamsForDepartment,
  filterAssignableUsersByTeam,
  normalizeProjectMembersResponse,
} from '../utils/assigneeTeamFilter';
import { filterEpicsForSprintPicker } from '../utils/epicSprintFilter';
import { issueStatusLabel, getWorkflowStatusOptions } from '../constants/issueStatuses';
import { DEFAULT_DEPARTMENT, normalizeDepartment } from '../constants/departments';
import { useDepartmentWorkflow } from '../hooks/useDepartmentWorkflow';
import { isSprintSteward, filterSprintsForBoardCarousel, canDeleteWorkItems, SPRINT_WORK_STATUSES } from '../constants/sprintAccess';
import { getDepartmentFormHints } from '../constants/departmentFormHints';
import { getStoredDepartmentView } from '../components/DepartmentSwitcher';
import SprintBurndown from '../components/SprintBurndown';
import Skeleton from '../components/ui/Skeleton';
import { COLORS as C, RADIUS } from '../constants/ui';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIORITY_META = {
  Blocker:  { color: C.danger, bg: C.dangerSoft, label: 'Blocker' },
  Critical: { color: C.danger, bg: C.dangerSoft, label: 'Critical' },
  High:     { color: C.warn, bg: C.warnSoft, label: 'High' },
  Medium:   { color: C.inkSoft, bg: C.sunken, label: 'Medium' },
  Low:      { color: C.inkFaint, bg: C.sunken, label: 'Low' },
};

const TYPE_ICON = { Bug: 'Bug', Task: 'Task', Story: 'Story', Epic: 'Epic' };

const BOARD_COLUMNS = ['Open', 'In Progress', 'Testing', 'Blocked', 'Done'];

/** Nearest Epic ancestor for a Story/Task/Bug (used for epic-scoped board). */
const rootEpicIdForIssue = (issue, issueById) => {
  let cur = issue;
  for (let n = 0; n < 30 && cur; n += 1) {
    if (cur.issuetype === 'Epic') return String(cur.issueid);
    const pid = cur.parent_id;
    if (pid == null || pid === '') return null;
    cur = issueById.get(String(pid));
  }
  return null;
};

/** Matches `displayBoard` pick: selected epic → that epic’s tree; none → issues with no epic ancestor. */
const issueInBoardEpicScope = (issue, issueById, selectedEpicId) => {
  const root = rootEpicIdForIssue(issue, issueById);
  if (selectedEpicId) return root === String(selectedEpicId);
  return root == null || root === '';
};

const EPIC_FLOW_COLUMNS = ['Planning', 'Active', 'Complete'];
const EPIC_FLOW_HEADER = { Planning: C.inkSoft, Active: C.done, Complete: C.accent };
// Valid statuses a user can set from the Kanban card (maps to CLOSED in backend → Done column)
const CARD_STATUSES = ['Open', 'In Progress', 'Testing', 'Blocked', 'Fixed', 'Closed'];
const CLOSED_ISSUE_STATUSES = ['Fixed', 'Not Reproducible', "Invalid", "Won't Fix", 'Duplicate', 'Closed'];
const COL_COLORS = {
  'Open': C.inkSoft,
  'In Progress': C.accent,
  'Testing': C.accent,
  'Blocked': C.danger,
  'Done': C.done,
};

/** Sprint statuses offered in the Edit Sprint form, in lifecycle order. */
const SPRINT_STATUS_OPTIONS = ['Backlog', 'Planning', 'Active', 'Review', 'Released', 'Closed'];

/**
 * Moving a sprint out of Released is the one status change with a consequence
 * beyond the sprint itself — the release record it produced keeps pointing at
 * it. Worth a warning, not a block: the user asked to be able to do this.
 */
const REOPENS_RELEASED = (from, to) => from === 'Released' && to !== 'Released';

const STATUS_BADGE = {
  // Muted on purpose — a Backlog sprint is a private draft, not something the
  // eye should land on first in the carousel.
  Backlog:  { bg: C.sunken, color: C.inkFaint },
  Planning: { bg: C.line, color: C.ink },
  Active:   { bg: C.doneSoft, color: C.done },
  Review:   { bg: C.warnSoft, color: C.warn },
  Released: { bg: C.accentSoft, color: C.accent },
  Closed:   { bg: C.sunken, color: C.inkSoft },
};

/** Strip trailing link blocks so title/body match IssueForm / TaskDetailModal. */
const stripStoryDescriptionLinkBlocks = (desc) => {
  if (!desc) return '';
  return String(desc)
    .replace(/\n\nLinks:\n.*$/s, '')
    .replace(/\n\nImage Links:\n.*$/s, '')
    .trim();
};

const storyTitleLineFromDescription = (desc) => {
  const c = stripStoryDescriptionLinkBlocks(desc);
  return c.split('\n')[0]?.trim() || c || 'Story';
};

const storyBodyAfterTitleFromDescription = (desc) => {
  const c = stripStoryDescriptionLinkBlocks(desc);
  const lines = c.split('\n');
  return lines.length > 1 ? lines.slice(1).join('\n').trim() : '';
};

const urlsFromDraftLinksField = (draftLinks) =>
  String(draftLinks || '')
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((u) => /^https?:\/\//i.test(u));

const normalizedUrlListKey = (urls) =>
  [...new Set((urls || []).map((u) => String(u).trim()).filter(Boolean))].sort().join('\n');

// ─── Shared styles ────────────────────────────────────────────────────────────

const btnStyle = (bg, outline = false) => ({
  background: outline ? 'white' : bg,
  color: outline ? bg : '#fff',
  border: outline ? `1.5px solid ${bg}` : 'none',
  borderRadius: 8, padding: '0.5rem 1rem',
  fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const dangerBtn = {
  background: C.dangerSoft, color: C.danger,
  border: `1px solid ${C.dangerSoft}`, borderRadius: 7,
  padding: '4px 10px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600,
};

const selectStyle = {
  padding: '0.5rem 0.9rem', borderRadius: 8, border: `1px solid ${C.line}`,
  background: '#fff', fontSize: '0.88rem', minWidth: 200, cursor: 'pointer',
};

const epicTitleAndDetails = (description) => {
  const desc = description || '';
  const firstNl = desc.indexOf('\n');
  return {
    title: (firstNl >= 0 ? desc.slice(0, firstNl) : desc).trim() || 'Epic',
    details: firstNl >= 0 ? desc.slice(firstNl + 1).trim() : '',
  };
};

/** Full-screen modal dimmer. Do not add onClick-to-close — accidental backdrop taps should not dismiss forms (use ✕ / Cancel). */
const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  padding: '1rem',
};

const modalBox = {
  background: '#fff', borderRadius: 16, padding: '1.75rem 2rem',
  width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
};

const labelStyle = {
  display: 'block', fontSize: '0.8rem', fontWeight: 600,
  color: C.ink, marginBottom: '0.25rem', marginTop: '0.75rem',
};

const inputStyle = {
  width: '100%', padding: '0.6rem 0.85rem', border: `1px solid ${C.line}`,
  borderRadius: 8, fontSize: '0.88rem', background: C.sunken, boxSizing: 'border-box',
};

/** Horizontal scroll for project / sprint cards (many items without wrapping). */
const pickerCarouselOuter = {
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  maxWidth: '100%',
  paddingBottom: 6,
  scrollbarWidth: 'thin',
};
const pickerCarouselInner = {
  display: 'flex',
  gap: 10,
  flexWrap: 'nowrap',
  width: 'max-content',
  paddingTop: 2,
  paddingLeft: 2,
  paddingRight: 2,
};

const CAROUSEL_EDGE_BTN_W = 48;
/** Show edge scroll arrows only when there are enough cards to justify horizontal navigation. */
const CAROUSEL_ARROW_MIN_COUNT = 3;

/** Scroll strip; horizontal padding is added in JSX only when edge arrows are visible. */
const pickerCarouselScrollTrack = {
  ...pickerCarouselOuter,
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
};

/** Wraps the scroll track + absolutely positioned ← / → on the strip edges. */
const pickerCarouselShell = {
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  overflow: 'visible',
};

const carouselEdgeButtonBase = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: CAROUSEL_EDGE_BTN_W,
  zIndex: 40,
  border: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: C.accentInk,
  color: '#fff',
  fontSize: '1.4rem',
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
  boxShadow: '0 2px 10px rgba(15, 23, 42, 0.25)',
  transition: 'opacity 0.15s, background 0.15s',
};

/**
 * Horizontal overflow strip with optional arrow navigation: ref, edge state, scrollStep(dir).
 * contentStamp should change when list items change so arrow enable/disable updates.
 */
function useCarouselScrollNav(contentStamp) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ canLeft: false, canRight: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) {
      setEdge({ canLeft: false, canRight: false });
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    setEdge({
      canLeft: scrollLeft > 4,
      canRight: scrollLeft < maxScroll - 4,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const schedule = () => requestAnimationFrame(update);
    schedule();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [update, contentStamp]);

  const scrollStep = useCallback((direction) => {
    const el = ref.current;
    if (!el) return;
    const step = Math.max(220, Math.floor(el.clientWidth * 0.72));
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  return { ref, edge, scrollStep };
}

// ─── Issue Card ───────────────────────────────────────────────────────────────

const IssueCard = ({
  issue,
  onStatusChange,
  onReassign,
  canEdit,
  canDelete,
  onDeleteStory,
  onMoveToSprint,
  onMoveToBacklog,
  sprintList,
  isInBacklog,
  allIssues,
  users = [],
  /** When set with `teamsMeta`, assignee list matches IssueForm / TaskDetailModal (project members + team filter). */
  baseAssignableUsers = null,
  teamsMeta = null,
  onOpenTaskModal,
  onOpenTaskDetail,
  storyReorder,
  taskReorder,
  boardDepartment = DEFAULT_DEPARTMENT,
}) => {
  const workflowOptions = useMemo(
    () => getWorkflowStatusOptions(boardDepartment),
    [boardDepartment]
  );
  const p = PRIORITY_META[issue.priority] || PRIORITY_META.Low;
  const [showMove, setShowMove] = useState(false);
  const [targetSprint, setTargetSprint] = useState('');
  
  // Get child tasks for Stories
  const isStory = issue.issuetype === 'Story';
  const childTasks = isStory && allIssues ? allIssues.filter(i => {
    // Handle both string and number comparison for parent_id
    return i.parent_id && (i.parent_id == issue.issueid || String(i.parent_id) === String(issue.issueid));
  }) : [];

  // Calculate story points for Story = sum of child tasks
  const displayStoryPoints = isStory && childTasks.length > 0
    ? childTasks.reduce((sum, task) => sum + (task.story_points || 0), 0)
    : issue.story_points;
  
  const assignableUsers = useMemo(() => {
    const nonAdmin = (list) =>
      (list || []).filter((u) => !['admin', 'superadmin'].includes(String(u.role || '').toLowerCase()));
    const base =
      baseAssignableUsers != null ? nonAdmin(baseAssignableUsers) : nonAdmin(users);
    const filtered =
      teamsMeta != null
        ? filterAssignableUsersByTeam(base, (issue.assigneeteam || '').trim(), teamsMeta)
        : base;
    if (!issue.assignee_id) return filtered;
    const aid = String(issue.assignee_id);
    if (filtered.some((u) => String(u.id) === aid)) return filtered;
    const cur = nonAdmin(users).find((u) => String(u.id) === aid);
    return cur ? [...filtered, cur] : filtered;
  }, [baseAssignableUsers, teamsMeta, issue.assigneeteam, issue.assignee_id, issue.issueid, users]);

  const btnReorder = {
    padding: '1px 0',
    width: 22,
    minHeight: 20,
    border: `1px solid ${C.upcoming}`,
    borderRadius: 4,
    background: C.sunken,
    fontSize: '0.68rem',
    fontWeight: 800,
    cursor: 'pointer',
    lineHeight: 1,
    color: C.inkSoft,
    flexShrink: 0,
  };

  const columnReorder = storyReorder || taskReorder;
  const reorderUpTitle = taskReorder
    ? 'Move up among sibling tasks'
    : 'Move up among stories under this epic';
  /** Arrow column: button width + gap so “N tasks” chip lines up with #id text when arrow is shown */
  const reorderRowOffset = columnReorder ? 28 : 0;

  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          padding: '0.8rem',
          border: `1px solid ${p.color}33`,
          borderLeft: `3px solid ${p.color}`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          cursor: 'pointer',
        }}
        onClick={() => {
          // Unify Story/Task details into TaskDetailModal.
          // Story kanban modal (TaskKanbanModal) is no longer the default story detail dialog.
          onOpenTaskDetail && onOpenTaskDetail(issue);
        }}
      >
      {/* Header: ↑ + #id row (center-aligned); task chip indented when ↑ visible; priority right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {columnReorder ? (
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  title={reorderUpTitle}
                  disabled={columnReorder.first}
                  onClick={(e) => {
                    e.stopPropagation();
                    columnReorder.onUp();
                  }}
                  style={{
                    ...btnReorder,
                    opacity: columnReorder.first ? 0.35 : 1,
                    cursor: columnReorder.first ? 'not-allowed' : 'pointer',
                  }}
                >
                  ↑
                </button>
              </div>
            ) : null}
            <div style={{ fontSize: '0.7rem', color: C.inkSoft, fontWeight: 600, lineHeight: 1.25, minWidth: 0, flex: 1 }}>
              #{issue.issueid} · {TYPE_ICON[issue.issuetype]} {issue.issuetype}
            </div>
          </div>
          {isStory && childTasks.length > 0 && (
            <div
              style={{
                fontSize: '0.65rem',
                color: C.accent,
                fontWeight: 600,
                background: C.accentSoft,
                padding: '2px 8px',
                borderRadius: 10,
                display: 'inline-block',
                width: 'fit-content',
                marginTop: 4,
                marginLeft: reorderRowOffset,
              }}
            >
              {childTasks.length} task{childTasks.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        <span style={{
          fontSize: '0.6rem',
          fontWeight: 700,
          padding: '2px 6px',
          borderRadius: 10,
          background: p.bg,
          color: p.color,
          flexShrink: 0,
        }}>
          {p.label}
        </span>
      </div>
      <p style={{ margin: '0.35rem 0 0.5rem', fontSize: '0.83rem', color: C.ink, fontWeight: 500, lineHeight: 1.4 }}>
        {isStory 
          ? (issue.description?.split('\n')[0] || issue.description || '(No description)')
          : (issue.description || '(No description)')
        }
      </p>
      <div style={{ fontSize: '0.72rem', color: C.inkSoft, marginBottom: 6 }}>
        {issue.assignee_name || issue.assignee_email
          ? `${issue.assignee_name || issue.assignee_email}`
          : `Team: ${issue.assigneeteam}`}
        {displayStoryPoints > 0 && (
          <span style={{ marginLeft: 8, background: C.accentSoft, color: C.accent, padding: '1px 5px', borderRadius: 10, fontWeight: 700 }}>
            {displayStoryPoints}pts{isStory && childTasks.length > 0 ? ' (sum)' : ''}
          </span>
        )}
      </div>
      {canEdit && (
        <>
          {/* Assignee Dropdown - Main Contact for Story */}
          <select 
            value={issue.assignee_id || ''}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { e.stopPropagation(); onReassign(issue.issueid, e.target.value); }}
            style={{ width: '100%', fontSize: '0.73rem', padding: '3px 6px', borderRadius: 6, border: `1px solid ${C.line}`, background: C.sunken, cursor: 'pointer', marginBottom: 4 }}>
            <option value="">👤 {isStory ? 'Main Contact...' : 'Assign to...'}</option>
            {assignableUsers.map(u => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </select>
          
          {/* Status Dropdown */}
          {CLOSED_ISSUE_STATUSES.includes(issue.status) ? (
            <div style={{ fontSize: '0.7rem', color: C.done, fontWeight: 700, padding: '2px 0 4px' }}>
              ✓ {issue.status} ·{' '}
              <button onClick={(e) => { e.stopPropagation(); onStatusChange(issue.issueid, 'Reopened'); }}
                style={{ background: 'none', border: 'none', color: C.warn, fontSize: '0.7rem', cursor: 'pointer', fontWeight: 700, padding: 0 }}>
                ↩ Reopen
              </button>
            </div>
          ) : (
            <select value={issue.status} 
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => { e.stopPropagation(); onStatusChange(issue.issueid, e.target.value); }}
              style={{ width: '100%', fontSize: '0.73rem', padding: '3px 6px', borderRadius: 6, border: `1px solid ${C.line}`, background: C.sunken, cursor: 'pointer', marginBottom: 4 }}>
              {workflowOptions.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
              <option disabled style={{ color: C.upcoming }}>──────</option>
              <option value={issue.issuetype === 'Bug' ? 'Fixed' : 'Closed'}>
                {issueStatusLabel('Done', boardDepartment)}
              </option>
            </select>
          )}
        </>
      )}
      {/* Move to Sprint (for backlog items) */}
      {isInBacklog && onMoveToSprint && sprintList?.length > 0 && (
        <div>
          {showMove ? (
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              <select value={targetSprint} onChange={e => setTargetSprint(e.target.value)}
                style={{ flex: 1, fontSize: '0.72rem', padding: '3px 6px', borderRadius: 6, border: `1px solid ${C.line}`, background: C.sunken }}>
                <option value="">Pick sprint…</option>
                {sprintList.filter(s => s.status !== 'Released' && s.status !== 'Closed').map(s => (
                  <option key={s.id} value={s.id}>{s.sprint_name} ({s.status})</option>
                ))}
              </select>
              <button onClick={() => { onMoveToSprint(issue.issueid, targetSprint); setShowMove(false); }}
                style={{ padding: '3px 8px', background: C.accentInk, color: '#fff', border: 'none', borderRadius: 5, fontSize: '0.7rem', cursor: 'pointer', fontWeight: 700 }}>
                Add
              </button>
              <button onClick={() => setShowMove(false)}
                style={{ padding: '3px 6px', background: C.sunken, border: `1px solid ${C.line}`, borderRadius: 5, fontSize: '0.7rem', cursor: 'pointer' }}>
                ✕
              </button>
            </div>
          ) : (
            <button onClick={() => setShowMove(true)}
              style={{ marginTop: 4, width: '100%', fontSize: '0.72rem', padding: '3px', border: `1px dashed ${C.upcoming}`, borderRadius: 6, background: 'white', color: C.inkSoft, cursor: 'pointer' }}>
              + Add to Sprint
            </button>
          )}
        </div>
      )}
      {/* Move to Backlog and Delete Story buttons moved to detail modals */}
      </div>
    </div>
  );
};

// ─── Epic workflow (Planning / Active / Complete) ─────────────────────────────

const EpicBoardStrip = ({
  epicsBoard,
  onSelectEpic,
  onOpenEpicModal,
  selectedEpicId,
  sprintLabelForEpic,
}) => {
  if (!epicsBoard || !EPIC_FLOW_COLUMNS.some((c) => (epicsBoard[c] || []).length > 0)) return null;

  return (
    <div style={{ marginBottom: '0.65rem', background: '#fff', borderRadius: 8, border: `1px solid ${C.line}`, padding: '0.5rem 0.6rem' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 800, color: C.inkSoft, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.45rem' }}>
        Epics
      </div>
      {/* auto-fit rather than a hard 3 — folds to 1-2 columns on narrow
          screens without needing to know the viewport. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.45rem' }}>
        {EPIC_FLOW_COLUMNS.map((col) => {
          const items = epicsBoard[col] || [];
          const headerColor = EPIC_FLOW_HEADER[col];
          return (
            <div key={col} style={{ background: C.sunken, borderRadius: 8, border: `1px solid ${C.line}`, overflow: 'hidden' }}>
              <div style={{ padding: '0.3rem 0.45rem', borderBottom: `2px solid ${headerColor}`, fontWeight: 800, fontSize: '0.7rem', color: headerColor }}>
                {col} <span style={{ opacity: 0.85 }}>({items.length})</span>
              </div>
              <div style={{ padding: '0.35rem', minHeight: 36 }}>
                {items.length === 0 ? (
                  <div style={{ fontSize: '0.65rem', color: C.upcoming, textAlign: 'center', padding: '0.2rem 0' }}>—</div>
                ) : (
                  items.map((epic) => {
                    const title = epic.description?.split('\n')[0] || epic.description || 'Epic';
                    const isSel = String(selectedEpicId) === String(epic.issueid);
                    const sprintLine = sprintLabelForEpic ? sprintLabelForEpic(epic) : '';
                    const tip = [sprintLine || null, 'Click to filter stories & tasks on the board below.'].filter(Boolean).join(' ');
                    return (
                      <div
                        key={epic.issueid}
                        role="button"
                        tabIndex={0}
                        onClick={() => onSelectEpic(epic.issueid)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') onSelectEpic(epic.issueid);
                        }}
                        title={tip}
                        style={{
                          background: '#fff',
                          borderRadius: 6,
                          padding: '0.3rem 0.4rem',
                          marginBottom: 4,
                          border: isSel ? `2px solid ${C.accent}` : `1px solid ${C.line}`,
                          boxShadow: '0 1px 1px rgba(0,0,0,0.04)',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 2 }}>
                          <div style={{ fontSize: '0.62rem', color: C.inkSoft, fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>
                            #{epic.issueid}
                            <span style={{ color: C.upcoming, fontWeight: 600, margin: '0 4px' }}>·</span>
                            <span style={{ color: C.inkFaint, fontWeight: 700 }}>Epic</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenEpicModal(epic.issueid);
                            }}
                            title="Epic details"
                            style={{
                              flexShrink: 0,
                              padding: '1px 6px',
                              borderRadius: 4,
                              border: `1px solid ${C.accentSoft}`,
                              background: C.accentSoft,
                              color: C.accentInk,
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              cursor: 'pointer',
                              lineHeight: 1.35,
                            }}
                          >
                            Open
                          </button>
                        </div>
                        <div
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            color: C.ink,
                            lineHeight: 1.25,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                          }}
                        >
                          {title}
                        </div>
                        {sprintLine ? (
                          <div
                            title={sprintLine}
                            style={{
                              marginTop: 3,
                              fontSize: '0.6rem',
                              color: C.inkSoft,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {sprintLine}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const acceptBrdFileForEpic = (file) => {
  if (!file) return null;
  const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
  const validExts = ['.pdf', '.docx', '.txt'];
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  if (!validTypes.includes(file.type) && !validExts.includes(ext)) {
    toast.error('Please upload a PDF, Word document, or text file');
    return null;
  }
  return file;
};

const AiBreakdownModePicker = ({ value, onChange, disabled, groupName = 'ai-breakdown-mode' }) => (
  <div style={{ marginTop: 10 }}>
    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.inkSoft, marginBottom: 6 }}>Breakdown style</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        fontSize: '0.78rem',
        color: C.ink,
        lineHeight: 1.35,
      }}>
        <input
          type="radio"
          name={groupName}
          checked={value === 'generic'}
          onChange={() => onChange('generic')}
          disabled={disabled}
          style={{ marginTop: 2 }}
        />
        <span><strong style={{ color: C.ink }}>Generic</strong> — feature stories (mixed teams: Frontend, Backend, QA…)</span>
      </label>
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        fontSize: '0.78rem',
        color: C.ink,
        lineHeight: 1.35,
      }}>
        <input
          type="radio"
          name={groupName}
          checked={value === 'team_tracks'}
          onChange={() => onChange('team_tracks')}
          disabled={disabled}
          style={{ marginTop: 2 }}
        />
        <span><strong style={{ color: C.ink }}>Team tracks</strong> — Backend, Web App (React), Mobile App (React Native); Blockers listed with Backend first</span>
      </label>
    </div>
  </div>
);

const EpicDetailModal = ({
  epic,
  activeSprint,
  projects,
  eligibleSprints,
  currentSprintId,
  sprintDisplayName,
  canEdit,
  onClose,
  onSaved,
  onMoveEpicToBacklog,
  onMoveEpicToSprint,
  onDeleteEpic,
  onNavigateToAI,
}) => {
  const desc = epic?.description || '';
  const firstNl = desc.indexOf('\n');
  const initialTitle = (firstNl >= 0 ? desc.slice(0, firstNl) : desc).trim() || 'Epic';
  const initialDetails = firstNl >= 0 ? desc.slice(firstNl + 1).trim() : '';

  const [epicTitle, setEpicTitle] = useState(initialTitle);
  const [epicDetails, setEpicDetails] = useState(initialDetails);
  const [epicStatus, setEpicStatus] = useState(epic?.epic_status || 'Planning');
  const [brdLink, setBrdLink] = useState('');
  const [figmaLink, setFigmaLink] = useState('');
  const [moveTargetSprint, setMoveTargetSprint] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingAi, setUploadingAi] = useState(false);
  const [aiPlanningEnabled, setAiPlanningEnabled] = useState(false);
  const [epicBrdFile, setEpicBrdFile] = useState(null);
  const [epicAiInputMode, setEpicAiInputMode] = useState('file'); // 'file' | 'text'
  const [epicBrdPasteText, setEpicBrdPasteText] = useState('');
  const [aiBreakdownMode, setAiBreakdownMode] = useState('generic');

  const switchEpicAiInputMode = (mode) => {
    setEpicAiInputMode(mode);
    if (mode === 'text') setEpicBrdFile(null);
    else setEpicBrdPasteText('');
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    if (!epic) return;
    const d = epic.description || '';
    const nl = d.indexOf('\n');
    setEpicTitle((nl >= 0 ? d.slice(0, nl) : d).trim() || 'Epic');
    setEpicDetails(nl >= 0 ? d.slice(nl + 1).trim() : '');
    setEpicStatus(epic.epic_status || 'Planning');
    const g = parseSprintGoal(activeSprint?.goal || '');
    setBrdLink(g.brdLink || '');
    setFigmaLink(g.figmaLink || '');
    const defaultSprint =
      epic.sprint_id != null && String(epic.sprint_id).trim() !== ''
        ? String(epic.sprint_id)
        : currentSprintId != null && String(currentSprintId).trim() !== ''
          ? String(currentSprintId)
          : '';
    setMoveTargetSprint(defaultSprint);
    setAiPlanningEnabled(false);
    setEpicBrdFile(null);
    setEpicAiInputMode('file');
    setEpicBrdPasteText('');
    setAiBreakdownMode('generic');
  }, [epic?.issueid, epic?.sprint_id, currentSprintId, activeSprint?.id, activeSprint?.goal]);

  if (!epic) return null;

  const moveSprintOptions = [...(eligibleSprints || [])].sort((a, b) =>
    (a.sprint_name || '').localeCompare(b.sprint_name || '', undefined, { sensitivity: 'base' }),
  );
  const epicAssignedSprintId =
    epic.sprint_id != null && String(epic.sprint_id).trim() !== '' ? String(epic.sprint_id) : null;
  const idsInMove = new Set(moveSprintOptions.map((s) => String(s.id)));
  const moveSelectOptions =
    epicAssignedSprintId && !idsInMove.has(epicAssignedSprintId)
      ? [
          {
            id: epicAssignedSprintId,
            sprint_name: sprintDisplayName || `Sprint #${epicAssignedSprintId}`,
            status: null,
            _currentOnly: true,
          },
          ...moveSprintOptions,
        ]
      : moveSprintOptions;
  const moveWouldBeNoOp =
    epicAssignedSprintId != null && String(moveTargetSprint) === epicAssignedSprintId;
  const parsedGoal = parseSprintGoal(activeSprint?.goal || '');
  const projectName = projects.find((p) => String(p.projectid) === String(epic.projectid))?.projectname;

  const handleSave = async () => {
    if (!canEdit) {
      onClose();
      return;
    }
    const titleTrim = (epicTitle || '').trim();
    if (!titleTrim) {
      toast.error('Epic title is required');
      return;
    }
    const detailsTrim = (epicDetails || '').trim();
    const nextDesc = detailsTrim ? `${titleTrim}\n${detailsTrim}` : titleTrim;
    const prevDesc = (epic.description || '').trim();

    setSaving(true);
    try {
      if (nextDesc !== prevDesc) {
        await updateIssue(epic.issueid, { description: nextDesc });
      }
      if ((epicStatus || 'Planning') !== (epic.epic_status || 'Planning')) {
        await updateEpicStatus(epic.issueid, epicStatus);
      }
      if (activeSprint?.id) {
        const nextGoal = composeSprintGoal(parsedGoal.goal, normalizeUrl(brdLink), normalizeUrl(figmaLink));
        const prevGoal = (activeSprint.goal || '').trim();
        if (nextGoal !== prevGoal) {
          await editSprint(activeSprint.id, {
            sprint_name: activeSprint.sprint_name,
            goal: nextGoal,
            start_date: activeSprint.start_date?.slice(0, 10),
            end_date: activeSprint.end_date?.slice(0, 10),
          });
        }
      }
      toast.success('Epic updated');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save epic');
    } finally {
      setSaving(false);
    }
  };

  const handleRunAi = async () => {
    if (!canEdit) return;
    if (!aiPlanningEnabled) {
      toast.error('Turn on AI Smart Planning first');
      return;
    }
    if (epicAiInputMode === 'file' && !epicBrdFile) {
      toast.error('Add a BRD file, or switch to “Paste text” and enter requirements.');
      return;
    }
    const pasted = epicBrdPasteText.trim();
    if (epicAiInputMode === 'text' && pasted.length < 50) {
      toast.error('Paste at least 50 characters of requirements (same minimum as an uploaded BRD).');
      return;
    }
    if (!(epicTitle || '').trim()) {
      toast.error('Epic title is required');
      return;
    }
    if (!currentSprintId) {
      toast.error('No sprint selected');
      return;
    }
    setUploadingAi(true);
    try {
      const genPayload = {
        sprintId: currentSprintId,
        sprintGoal: parsedGoal.goal || activeSprint?.sprint_name || '',
        projectName,
        epicIssueId: epic.issueid,
        epicTitle: (epicTitle || '').trim(),
        breakdownMode: aiBreakdownMode,
      };
      if (epicAiInputMode === 'text') {
        await generateTasksFromBRD({ ...genPayload, brdText: pasted });
      } else {
        const uploadRes = await uploadBRD(epicBrdFile, currentSprintId, epic.issueid);
        toast.success('Epic BRD uploaded!');
        await generateTasksFromBRD({ ...genPayload, documentId: uploadRes.data.document.id });
      }
      toast.success('AI generated drafts for this epic — review and approve.');
      setEpicBrdFile(null);
      setEpicBrdPasteText('');
      onSaved();
      if (onNavigateToAI) onNavigateToAI(currentSprintId, epic.issueid);
    } catch (err) {
      toast.error(err.response?.data?.message || 'BRD / AI generation failed');
    } finally {
      setUploadingAi(false);
    }
  };

  const epicDisplayTitle = (epic.description || '').split('\n')[0] || epic.description || 'Epic';

  return (
    <div style={modalOverlay}>
      <div
        style={{
          ...modalBox,
          maxWidth: 720,
          padding: '1.5rem',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <h3 style={{ margin: 0, color: C.ink, fontWeight: 800, fontSize: '1.05rem' }}>Epic · #{epic.issueid}</h3>
            <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: C.inkSoft }}>
              Sprint: <strong style={{ color: C.ink }}>{sprintDisplayName}</strong>
            </p>
            <SprintReferenceLinks
              brdLink={brdLink}
              figmaLink={figmaLink}
              size="md"
              style={{ marginTop: 8 }}
            />
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.inkFaint }}>✕</button>
        </div>

        <label style={{ ...labelStyle, marginTop: 0 }}>Epic title</label>
        <input
          style={{ ...inputStyle, background: canEdit ? C.sunken : C.sunken }}
          value={epicTitle}
          onChange={(e) => setEpicTitle(e.target.value)}
          disabled={!canEdit}
          placeholder="Short epic name for the board"
        />

        <label style={labelStyle}>Description (optional details)</label>
        <textarea
          style={{ ...inputStyle, resize: 'vertical', minHeight: 72, background: canEdit ? C.sunken : C.sunken }}
          value={epicDetails}
          onChange={(e) => setEpicDetails(e.target.value)}
          disabled={!canEdit}
          placeholder="Extra context (not shown as the card title)"
        />

        <label style={labelStyle}>Epic workflow</label>
        <select
          style={inputStyle}
          value={epicStatus}
          onChange={(e) => setEpicStatus(e.target.value)}
          disabled={!canEdit}
        >
          <option value="Planning">Planning</option>
          <option value="Active">Active</option>
          <option value="Complete">Complete</option>
        </select>

        <p style={{ margin: '10px 0 0', fontSize: '0.72rem', color: C.inkSoft, lineHeight: 1.45 }}>
          BRD and Figma links below are stored on the <strong>sprint</strong> (shared reference for this sprint). Editing them here updates the sprint goal links.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <SprintGoalLinkInput
            label="BRD link (optional)"
            value={brdLink}
            onChange={(e) => setBrdLink(e.target.value)}
            disabled={!canEdit}
            inputStyle={{ ...inputStyle, background: canEdit ? C.sunken : C.sunken }}
            labelStyle={{ ...labelStyle, marginTop: 8 }}
          />
          <SprintGoalLinkInput
            label="Figma link (optional)"
            value={figmaLink}
            onChange={(e) => setFigmaLink(e.target.value)}
            disabled={!canEdit}
            inputStyle={{ ...inputStyle, background: canEdit ? C.sunken : C.sunken }}
            labelStyle={{ ...labelStyle, marginTop: 8 }}
          />
        </div>

        {canEdit && (
          <div style={{
            marginTop: 14, padding: '12px 14px',
            background: aiPlanningEnabled ? C.doneSoft : '#fff',
            border: `1px solid ${aiPlanningEnabled ? C.done : C.line}`,
            borderRadius: 10,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: aiPlanningEnabled ? 12 : 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: C.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
                  🤖 AI Smart Planning
                </div>
                <div style={{ fontSize: '0.75rem', color: C.inkSoft, marginTop: 2 }}>
                  Upload a BRD or paste requirements — AI drafts stories and tasks under this epic
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = !aiPlanningEnabled;
                  setAiPlanningEnabled(next);
                  if (!next) {
                    setEpicBrdFile(null);
                    setEpicAiInputMode('file');
                    setEpicBrdPasteText('');
                    setAiBreakdownMode('generic');
                  }
                }}
                style={{
                  width: 48, height: 26, borderRadius: 13, border: 'none',
                  background: aiPlanningEnabled ? C.done : C.upcoming,
                  cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <span style={{
                  position: 'absolute', top: 3, left: aiPlanningEnabled ? 25 : 3,
                  width: 20, height: 20, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                }} />
              </button>
            </div>
            {aiPlanningEnabled && (
              <div>
                <AiBreakdownModePicker groupName="epic-ai-breakdown" value={aiBreakdownMode} onChange={setAiBreakdownMode} disabled={uploadingAi} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10, marginBottom: 8 }}>
                  <button
                    type="button"
                    disabled={uploadingAi}
                    onClick={() => switchEpicAiInputMode('file')}
                    style={{
                      flex: 1,
                      padding: '0.45rem 0.5rem',
                      borderRadius: 8,
                      border: `1px solid ${epicAiInputMode === 'file' ? C.done : C.line}`,
                      background: epicAiInputMode === 'file' ? C.doneSoft : '#fff',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      color: C.ink,
                      cursor: uploadingAi ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Upload file
                  </button>
                  <button
                    type="button"
                    disabled={uploadingAi}
                    onClick={() => switchEpicAiInputMode('text')}
                    style={{
                      flex: 1,
                      padding: '0.45rem 0.5rem',
                      borderRadius: 8,
                      border: `1px solid ${epicAiInputMode === 'text' ? C.done : C.line}`,
                      background: epicAiInputMode === 'text' ? C.doneSoft : '#fff',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      color: C.ink,
                      cursor: uploadingAi ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Paste text
                  </button>
                </div>
                {epicAiInputMode === 'file' ? (
                  <>
                    <label style={{ ...labelStyle, marginTop: 0 }}>BRD document *</label>
                    <div
                      style={{
                        border: `2px dashed ${C.upcoming}`, borderRadius: 8, padding: '14px',
                        textAlign: 'center', background: '#fff', cursor: 'pointer',
                      }}
                      onClick={() => document.getElementById('epic-modal-brd-input')?.click()}
                    >
                      <input
                        id="epic-modal-brd-input"
                        type="file"
                        accept=".pdf,.docx,.txt"
                        onChange={(e) => {
                          const f = acceptBrdFileForEpic(e.target.files?.[0]);
                          if (f) setEpicBrdFile(f);
                        }}
                        style={{ display: 'none' }}
                      />
                      {epicBrdFile ? (
                        <div>
                          <div style={{ fontWeight: 600, color: C.ink }}>{epicBrdFile.name}</div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setEpicBrdFile(null); }}
                            style={{ marginTop: 8, fontSize: '0.75rem', color: C.danger, background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            Remove file
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontWeight: 600, color: C.inkSoft, fontSize: '0.85rem' }}>Click to upload BRD</div>
                          <div style={{ fontSize: '0.72rem', color: C.inkFaint, marginTop: 4 }}>PDF, Word, or Text</div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <label style={{ ...labelStyle, marginTop: 0 }}>Requirements / BRD text *</label>
                    <textarea
                      value={epicBrdPasteText}
                      onChange={(e) => setEpicBrdPasteText(e.target.value)}
                      disabled={uploadingAi}
                      rows={10}
                      placeholder="Paste scope, user stories, APIs, acceptance criteria…"
                      style={{
                        ...inputStyle,
                        resize: 'vertical',
                        minHeight: 140,
                        fontSize: '0.82rem',
                        lineHeight: 1.45,
                      }}
                    />
                    <p style={{ margin: '6px 0 0', fontSize: '0.7rem', color: C.inkSoft }}>
                      Minimum 50 characters (server rule). Current: {epicBrdPasteText.trim().length}
                    </p>
                  </>
                )}
                <button
                  type="button"
                  disabled={
                    uploadingAi
                    || (epicAiInputMode === 'file' && !epicBrdFile)
                    || (epicAiInputMode === 'text' && epicBrdPasteText.trim().length < 50)
                  }
                  onClick={handleRunAi}
                  style={{
                    marginTop: 10, width: '100%', padding: '0.55rem', borderRadius: 8, border: 'none',
                    background: uploadingAi ? C.inkFaint : C.done, color: '#fff', fontWeight: 700, cursor: uploadingAi ? 'not-allowed' : 'pointer',
                  }}
                >
                  {uploadingAi
                    ? 'Processing…'
                    : epicAiInputMode === 'text'
                      ? 'Generate AI drafts from text'
                      : 'Upload BRD & generate AI drafts'}
                </button>
              </div>
            )}
          </div>
        )}

        {canEdit && moveSelectOptions.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
            <label style={{ ...labelStyle, marginTop: 0 }}>Move epic to another sprint</label>
            <p style={{ margin: '4px 0 8px', fontSize: '0.72rem', color: C.inkSoft, lineHeight: 1.4 }}>
              Current assignment is selected below. Pick a different sprint and click Move to reassign.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                style={{ ...inputStyle, flex: 1, marginTop: 0 }}
                value={moveTargetSprint}
                onChange={(e) => setMoveTargetSprint(e.target.value)}
              >
                {moveSelectOptions.map((s) => (
                  <option key={`${s.id}${s._currentOnly ? '-cur' : ''}`} value={String(s.id)}>
                    {s._currentOnly
                      ? `${s.sprint_name} — current`
                      : `${s.sprint_name} (${s.status})${
                          epicAssignedSprintId && String(s.id) === epicAssignedSprintId ? ' — current' : ''
                        }`}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!moveTargetSprint || moveWouldBeNoOp}
                onClick={() => {
                  if (!moveTargetSprint) {
                    toast.error('Choose a sprint');
                    return;
                  }
                  if (moveWouldBeNoOp) {
                    toast.error('Select a different sprint than the epic’s current one');
                    return;
                  }
                  onMoveEpicToSprint(epic.issueid, Number(moveTargetSprint));
                }}
                style={{
                  ...btnStyle(C.accentInk),
                  marginTop: 0,
                  flexShrink: 0,
                  opacity: !moveTargetSprint || moveWouldBeNoOp ? 0.5 : 1,
                  cursor: !moveTargetSprint || moveWouldBeNoOp ? 'not-allowed' : 'pointer',
                }}
              >
                Move
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20, alignItems: 'center' }}>
          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => {
                  const t = (epic.description || '').split('\n')[0] || epic.description || 'Epic';
                  if (!window.confirm(`Move epic "${t}" to backlog?\n\nStories and tasks under this epic will also move to backlog.`)) return;
                  onMoveEpicToBacklog(epic.issueid);
                }}
                style={{
                  padding: '0.55rem 0.9rem', borderRadius: 8, border: `1px dashed ${C.warn}`,
                  background: C.warnSoft, color: C.warn, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                }}
              >
                → Move to backlog
              </button>
              <button
                type="button"
                onClick={() => onDeleteEpic(epic.issueid, epicDisplayTitle)}
                style={{
                  padding: '0.55rem 0.9rem', borderRadius: 8, border: `1px solid ${C.dangerSoft}`,
                  background: C.dangerSoft, color: C.danger, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                }}
              >
                🗑 Delete epic
              </button>
            </>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={{ ...btnStyle(C.inkFaint, true) }}>Close</button>
          {canEdit && (
            <button
              type="button"
              disabled={saving || uploadingAi}
              onClick={handleSave}
              style={{ ...btnStyle(C.accentInk) }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Task Kanban Modal (story detail) — action row control heights (align with TaskDetailModal) ─
const KANBAN_STORY_ACTION_H = 38;
const kanbanStoryActionBtnBase = {
  minHeight: KANBAN_STORY_ACTION_H,
  height: KANBAN_STORY_ACTION_H,
  padding: '0 14px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  borderRadius: 8,
  fontSize: '0.8rem',
  fontWeight: 600,
};
const kanbanStoryActionSelectBase = {
  height: KANBAN_STORY_ACTION_H,
  minHeight: KANBAN_STORY_ACTION_H,
  padding: '0 10px',
  fontSize: '0.8rem',
  fontWeight: 500,
  borderRadius: 8,
  border: `1px solid ${C.line}`,
  background: C.sunken,
  boxSizing: 'border-box',
};

// ─── Task Kanban Modal ────────────────────────────────────────────────────────

const TaskKanbanModal = ({
  story,
  tasks,
  users,
  onClose,
  onStatusChange,
  onReassign,
  sprintId,
  projectId,
  onTaskCreated,
  onStoryUpdated,
  onDeleteStory,
  onMoveToBacklog,
  canDelete = false,
  allBoardIssues = [],
  onMoveTaskToStory = null,
  canEditTaskDetail = false,
  /** Story → Epic link in story modal (managers only); task attach-to-story uses canEditTaskDetail. */
  canEditStoryEpic = false,
  onDeleteTask = null,
  boardDepartment = DEFAULT_DEPARTMENT,
}) => {
  const isMobile = useIsMobile();
  const workflowOptions = useMemo(
    () => getWorkflowStatusOptions(boardDepartment),
    [boardDepartment]
  );
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [savingStory, setSavingStory] = useState(false);
  const [converting, setConverting] = useState(false);
  const TASK_COLUMNS = ['Open', 'In Progress', 'Testing', 'Blocked', 'Done'];
  const TASK_COL_COLORS = {
    'Open': C.inkSoft,
    'In Progress': C.accent,
    'Testing': C.accent,
    'Blocked': C.danger,
    'Done': C.done,
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Group tasks by status
  const taskBoard = {};
  TASK_COLUMNS.forEach(col => taskBoard[col] = []);
  tasks.forEach(task => {
    let col;
    if (CLOSED_ISSUE_STATUSES.includes(task.status)) col = 'Done';
    else if (TASK_COLUMNS.includes(task.status)) col = task.status;
    else col = 'Open';
    taskBoard[col].push(task);
  });

  const visibleTaskColumns =
    (taskBoard.Blocked || []).length > 0
      ? TASK_COLUMNS
      : TASK_COLUMNS.filter((c) => c !== 'Blocked');

  const openTaskSnapshotKey = useMemo(() => {
    if (!selectedTask) return '';
    const t = tasks.find((x) => String(x.issueid) === String(selectedTask.issueid));
    if (!t) return '';
    return `${t.issueid}|${Number(t.story_points) || 0}|${t.status}|${t.assignee_id ?? ''}|${t.priority ?? ''}`;
  }, [tasks, selectedTask?.issueid]);

  useEffect(() => {
    if (!openTaskSnapshotKey) return;
    const issueId = openTaskSnapshotKey.split('|')[0];
    const fresh = tasks.find((x) => String(x.issueid) === issueId);
    if (fresh) setSelectedTask(fresh);
  }, [openTaskSnapshotKey]);

  const storyTitle = storyTitleLineFromDescription(story.description);
  const storyPriority = PRIORITY_META[story.priority] || PRIORITY_META.High;
  const storyDue = story.due_date ? story.due_date.split('T')[0] : null;
  const storyAssigneeName = story.assignee_name || story.assignee_email || null;

  // Editable draft states for story fields
  const [draftTitle, setDraftTitle] = useState(storyTitle);
  const [draftStoryPoints, setDraftStoryPoints] = useState(() => String(Number(story.story_points) || 0));
  const [draftDueDate, setDraftDueDate] = useState(storyDue || '');
  const [draftPriority, setDraftPriority] = useState(story.priority || 'High');
  const [draftAssignee, setDraftAssignee] = useState(String(story.assignee_id || ''));
  const [draftTeam, setDraftTeam] = useState(story.assigneeteam || '');
  const [draftStatus, setDraftStatus] = useState(story.status || 'Open');
  const storyParentIssue = useMemo(
    () => (allBoardIssues || []).find((i) => String(i.issueid) === String(story.parent_id)),
    [allBoardIssues, story.parent_id],
  );
  const [draftEpicParent, setDraftEpicParent] = useState(() =>
    storyParentIssue?.issuetype === 'Epic' ? String(story.parent_id) : '',
  );
  const [draftDescription, setDraftDescription] = useState(() =>
    storyBodyAfterTitleFromDescription(story.description),
  );
  const [draftLinks, setDraftLinks] = useState(() => {
    // Extract links from description on initial load
    const matches = (story.description || '').match(/https?:\/\/[^\s,]+/gi) || [];
    return matches.join(', ');
  });
  const [editLinksMode, setEditLinksMode] = useState(false);

  const { data: projectMembersData } = useQuery({
    queryKey: ['project-members', story.projectid],
    queryFn: () => api.get(`/projects/${story.projectid}/members`).then((r) => r.data),
    enabled: !!story.projectid,
  });
  const { data: customTeamsStory = [] } = useQuery({
    queryKey: ['custom-teams'],
    queryFn: getCustomTeams,
  });
  const storyModalTeams = useMemo(
    () => mergeTeamsForDepartment(customTeamsStory, boardDepartment),
    [customTeamsStory, boardDepartment],
  );
  const storyProjectMembers = normalizeProjectMembersResponse(projectMembersData);
  const storyBaseAssignable = useMemo(() => {
    const base = storyProjectMembers.length > 0
      ? storyProjectMembers.filter((u) => !['admin', 'superadmin'].includes(u.role?.toLowerCase()))
      : users.filter((u) => !['admin', 'superadmin'].includes(u.role?.toLowerCase()));
    return base;
  }, [storyProjectMembers, users]);
  const storyFilteredAssignable = useMemo(
    () => filterAssignableUsersByTeam(storyBaseAssignable, draftTeam, storyModalTeams),
    [storyBaseAssignable, draftTeam, storyModalTeams],
  );

  const assignableForTaskRow = useCallback(
    (task) => {
      const nonAdmin = (list) =>
        (list || []).filter(
          (u) => !['admin', 'superadmin'].includes(String(u.role || '').toLowerCase()),
        );
      const teamKey =
        task.assigneeteam && String(task.assigneeteam).trim()
          ? task.assigneeteam
          : draftTeam;
      const filtered = filterAssignableUsersByTeam(
        storyBaseAssignable,
        String(teamKey || '').trim(),
        storyModalTeams,
      );
      if (!task.assignee_id) return filtered;
      const aid = String(task.assignee_id);
      if (filtered.some((u) => String(u.id) === aid)) return filtered;
      const cur = nonAdmin(users).find((u) => String(u.id) === aid);
      return cur ? [...filtered, cur] : filtered;
    },
    [storyBaseAssignable, storyModalTeams, draftTeam, users],
  );

  const { data: epicsForStoryData = [], isPending: epicsForStoryLoading } = useQuery({
    queryKey: ['project-epics-story-modal', story.projectid],
    queryFn: () => getEpicsForProject(story.projectid).then((r) => r.data),
    enabled: !!story.projectid,
  });
  const epicsForStory = useMemo(
    () => filterEpicsForSprintPicker(epicsForStoryData, story.sprint_id),
    [epicsForStoryData, story.sprint_id],
  );

  // Child task count for conversion guard
  const childTaskCount = tasks.length;

  // Update drafts when story prop changes
  useEffect(() => {
    setDraftTitle(storyTitleLineFromDescription(story.description));
    setDraftStoryPoints(String(Number(story.story_points) || 0));
    setDraftDueDate(story.due_date ? story.due_date.split('T')[0] : '');
    setDraftPriority(story.priority || 'High');
    setDraftAssignee(String(story.assignee_id || ''));
    setDraftTeam(story.assigneeteam || '');
    setDraftStatus(story.status || 'Open');
    setDraftDescription(storyBodyAfterTitleFromDescription(story.description));
    const matches = (story.description || '').match(/https?:\/\/[^\s,]+/gi) || [];
    setDraftLinks(matches.join(', '));
    setEditLinksMode(false);
  }, [story.issueid, story.description, story.story_points, story.due_date, story.priority, story.assignee_id, story.assigneeteam, story.status]);

  useEffect(() => {
    if (!draftAssignee) return;
    const ok = storyFilteredAssignable.some((u) => String(u.id) === String(draftAssignee));
    if (!ok) setDraftAssignee('');
  }, [story.issueid, storyFilteredAssignable, draftAssignee]);

  useEffect(() => {
    if (storyParentIssue?.issuetype === 'Epic') {
      setDraftEpicParent(String(story.parent_id));
    } else {
      setDraftEpicParent('');
    }
  }, [story.issueid, story.parent_id, storyParentIssue?.issuetype]);

  const savedStoryEpicParentId =
    storyParentIssue?.issuetype === 'Epic' ? String(story.parent_id) : '';

  const storySavePayload = useMemo(() => {
    const payload = {};
    const savedPts = Number(story.story_points) || 0;
    const savedDue = story.due_date ? story.due_date.split('T')[0] : '';
    const savedPriority = story.priority || 'High';
    const savedAssignee = String(story.assignee_id || '');
    const savedTeam = story.assigneeteam || '';
    const savedStatus = story.status || 'Open';

    const bodyTrim = draftDescription.trim();
    const links = urlsFromDraftLinksField(draftLinks);
    let newDescription = draftTitle.trim();
    if (bodyTrim) newDescription += `\n${bodyTrim}`;
    if (links.length > 0) newDescription += `\n\nLinks:\n${links.map((l) => `- ${l}`).join('\n')}`;

    const savedTitleCmp = storyTitleLineFromDescription(story.description).trim();
    const savedBodyCmp = storyBodyAfterTitleFromDescription(story.description).trim();
    const savedUrlsKey = normalizedUrlListKey(extractUrls(story.description));
    const draftUrlsKey = normalizedUrlListKey(links);
    const descriptionDirty =
      draftTitle.trim() !== savedTitleCmp
      || bodyTrim !== savedBodyCmp
      || draftUrlsKey !== savedUrlsKey;

    if (descriptionDirty) {
      payload.description = newDescription.trim();
    }
    const pts = parseInt(String(draftStoryPoints).trim(), 10);
    if (!Number.isNaN(pts) && pts !== savedPts) {
      payload.story_points = Math.max(0, Math.min(100, pts));
    }
    if (draftDueDate !== savedDue) {
      payload.due_date = draftDueDate || null;
    }
    if (draftPriority !== savedPriority) {
      payload.priority = draftPriority;
    }
    if (draftAssignee !== savedAssignee) {
      payload.assignee_id = draftAssignee || null;
    }
    if (draftTeam !== savedTeam) {
      payload.assigneeteam = draftTeam || null;
    }
    if (draftStatus !== savedStatus) {
      payload.status = draftStatus;
    }
    if (
      canEditStoryEpic &&
      String(draftEpicParent || '') !== String(savedStoryEpicParentId || '')
    ) {
      payload.parent_id = draftEpicParent ? Number(draftEpicParent) : null;
    }
    return payload;
  }, [
    story.issueid,
    story.description,
    story.story_points,
    story.due_date,
    story.priority,
    story.assignee_id,
    story.assigneeteam,
    story.status,
    draftTitle,
    draftDescription,
    draftLinks,
    draftStoryPoints,
    draftDueDate,
    draftPriority,
    draftAssignee,
    draftTeam,
    draftStatus,
    draftEpicParent,
    savedStoryEpicParentId,
    canEditStoryEpic,
  ]);

  const storyIsDirty = Object.keys(storySavePayload).length > 0;

  const handleSaveStoryDetails = async () => {
    if (Object.keys(storySavePayload).length === 0) {
      toast('No changes to save');
      return;
    }
    setSavingStory(true);
    try {
      await api.put(`/issues/${story.issueid}`, { ...storySavePayload });
      toast.success('Story updated');
      onStoryUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update story');
    }
    setSavingStory(false);
  };

  const storyUrls = extractUrls(story.description);

  const handleConvertToTask = async () => {
    if (childTaskCount > 0) {
      toast.error('Cannot convert: Story has child tasks. Remove them first.');
      return;
    }
    if (!window.confirm('Convert this Story to a Task?')) return;
    setConverting(true);
    try {
      await api.put(`/issues/${story.issueid}`, { issuetype: 'Task' });
      toast.success('Converted to Task');
      onStoryUpdated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Conversion failed');
    }
    setConverting(false);
  };

  return (
    <div style={modalOverlay}>
      <div
        style={{
          ...modalBox,
          maxWidth: 1200,
          padding: '1.5rem',
          border: '2px solid transparent',
          borderColor: storyIsDirty ? C.warn : 'transparent',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s ease',
        }}
      >
        {/* Header — wraps responsively so the Add Task button is always visible (all roles). */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.75rem',
            flexWrap: 'wrap',
            rowGap: 8,
            columnGap: 12,
          }}
        >
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: C.inkSoft, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Story #{story.issueid}
            </div>
            <input
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 700,
                color: C.ink,
                border: 'none',
                borderBottom: '2px solid transparent',
                outline: 'none',
                width: '100%',
                minWidth: 0,
                background: 'transparent',
                padding: '2px 0',
                borderRadius: 0,
              }}
              onFocus={(e) => { e.target.style.borderBottomColor = C.accent; }}
              onBlur={(e) => { e.target.style.borderBottomColor = 'transparent'; }}
              placeholder="Story title"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={() => setShowAddTask(true)}
              title="Add a task under this story"
              style={{ padding: '0.45rem 1rem', background: C.accentInk, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              + Add Task
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: C.inkFaint }}>×</button>
          </div>
        </div>

        {/* Meta info row - Type, Project, Sprint, Creator */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: '0.5rem', padding: '4px 0', borderBottom: `1px solid ${C.sunken}` }}>
          {/* Type badge */}
          <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: C.accentSoft, color: C.accentInk }}>
            📖 Story
          </span>
          {/* Project */}
          {story.projectname && (
            <span style={{ fontSize: '0.72rem', color: C.inkSoft }}>
              📁 {story.projectname}
            </span>
          )}
          {/* Sprint */}
          {story.sprint_name && (
            <span style={{ fontSize: '0.72rem', color: C.inkSoft }}>
              🏃 {story.sprint_name}
            </span>
          )}
          {/* Creator */}
          {story.creator_name || story.creator_email ? (
            <span style={{ fontSize: '0.72rem', color: C.inkSoft }}>
              👤 Created by {story.creator_name || story.creator_email}
            </span>
          ) : null}
        </div>

        {/* Story Details — layout aligned with Task detail modal (grid + separate actions) */}
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: C.inkSoft, marginBottom: '0.75rem' }}>
            Details
            <span style={{ fontWeight: 500, color: C.inkFaint, marginLeft: 8, fontSize: '0.82rem' }}>
              · {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
            </span>
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.inkSoft, fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority</label>
              <select
                value={draftPriority}
                onChange={(e) => setDraftPriority(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '0.85rem',
                  border: `1px solid ${C.line}`,
                  borderRadius: 6,
                  background: C.sunken,
                  boxSizing: 'border-box',
                }}
              >
                <option value="Blocker">Blocker</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.inkSoft, fontWeight: 600, display: 'block', marginBottom: 4 }}>Status</label>
              {CLOSED_ISSUE_STATUSES.includes(draftStatus) ? (
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: C.done, padding: '6px 0' }}>
                  ✓ {draftStatus}
                  <button
                    type="button"
                    onClick={() => setDraftStatus('Reopened')}
                    style={{
                      marginLeft: 8,
                      background: 'none',
                      border: 'none',
                      color: C.warn,
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      fontWeight: 700,
                      padding: 0,
                    }}
                  >
                    ↩ Reopen
                  </button>
                </div>
              ) : (
                <select
                  value={draftStatus}
                  onChange={(e) => setDraftStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: '0.85rem',
                    border: `1px solid ${C.line}`,
                    borderRadius: 6,
                    background: C.sunken,
                    boxSizing: 'border-box',
                  }}
                >
                  {workflowOptions.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                  <option value="Reopened">Reopened</option>
                  <option disabled style={{ color: C.upcoming }}>────────</option>
                  <option value="Closed">{issueStatusLabel('Done', boardDepartment)}</option>
                </select>
              )}
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.inkSoft, fontWeight: 600, display: 'block', marginBottom: 4 }}>Story points</label>
              <input
                type="number"
                min={0}
                max={100}
                value={draftStoryPoints}
                onChange={(e) => setDraftStoryPoints(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '0.85rem',
                  border: `1px solid ${C.line}`,
                  borderRadius: 6,
                  background: C.sunken,
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.inkSoft, fontWeight: 600, display: 'block', marginBottom: 4 }}>ETA (Due Date)</label>
              <input
                type="date"
                value={draftDueDate}
                onChange={(e) => setDraftDueDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '0.85rem',
                  border: `1px solid ${C.line}`,
                  borderRadius: 6,
                  background: C.sunken,
                  boxSizing: 'border-box',
                }}
              />
              {draftDueDate && (
                <span style={{ fontSize: '0.72rem', color: new Date(draftDueDate) < new Date() ? C.danger : C.done, fontWeight: 600, display: 'block', marginTop: 4 }}>
                  {new Date(draftDueDate) < new Date() ? 'Overdue' : ''}
                </span>
              )}
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.inkSoft, fontWeight: 600, display: 'block', marginBottom: 4 }}>Team</label>
              <select
                value={draftTeam}
                onChange={(e) => {
                  setDraftTeam(e.target.value);
                  setDraftAssignee('');
                }}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '0.85rem',
                  border: `1px solid ${C.line}`,
                  borderRadius: 6,
                  background: C.sunken,
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Select team…</option>
                {storyModalTeams.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: C.inkSoft, fontWeight: 600, display: 'block', marginBottom: 4 }}>Assignee</label>
              <select
                value={draftAssignee}
                onChange={(e) => setDraftAssignee(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '0.85rem',
                  border: `1px solid ${C.line}`,
                  borderRadius: 6,
                  background: C.sunken,
                  boxSizing: 'border-box',
                }}
              >
                <option value="">
                  {storyFilteredAssignable.length === 0 ? 'No people in this team' : 'Unassigned'}
                </option>
                {storyFilteredAssignable.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.name || u.email}</option>
                ))}
              </select>
              {draftTeam && draftTeam !== 'Any' && storyFilteredAssignable.length === 0 && (
                <span style={{ fontSize: '0.72rem', color: C.warn, fontWeight: 600, display: 'block', marginTop: 4 }}>
                  No project members on {draftTeam} yet.
                </span>
              )}
            </div>
            {story.projectid && !canEditStoryEpic && storyParentIssue?.issuetype === 'Epic' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '0.75rem', color: C.inkSoft, fontWeight: 600, display: 'block', marginBottom: 4 }}>Epic</label>
                <div style={{ fontSize: '0.85rem', color: C.ink, fontWeight: 600 }}>
                  #{story.parent_id}{' '}
                  {(storyParentIssue.description || '').split('\n')[0]?.slice(0, 72) || ''}
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 10,
              rowGap: 10,
              minWidth: 0,
            }}
          >
            <button
              type="button"
              onClick={handleSaveStoryDetails}
              disabled={!storyIsDirty || savingStory}
              style={{
                ...kanbanStoryActionBtnBase,
                flexShrink: 0,
                padding: '0 18px',
                border: 'none',
                fontWeight: 700,
                background: (!storyIsDirty || savingStory) ? C.upcoming : C.accentInk,
                color: '#fff',
                cursor: (!storyIsDirty || savingStory) ? 'not-allowed' : 'pointer',
              }}
            >
              {savingStory ? 'Saving…' : 'Save changes'}
            </button>
            {childTaskCount === 0 && (
              <button
                type="button"
                onClick={handleConvertToTask}
                disabled={converting}
                style={{
                  ...kanbanStoryActionBtnBase,
                  flexShrink: 0,
                  border: `1px solid ${C.upcoming}`,
                  background: C.sunken,
                  color: C.ink,
                  cursor: converting ? 'not-allowed' : 'pointer',
                }}
              >
                {converting ? '…' : '↔ Convert to Task'}
              </button>
            )}
            {onMoveToBacklog && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Move story "${storyTitle}" to backlog?\n\nTasks under this story will also be moved to backlog.`)) {
                    onMoveToBacklog(story.issueid);
                    onClose();
                  }
                }}
                style={{
                  ...kanbanStoryActionBtnBase,
                  flexShrink: 0,
                  border: `1px dashed ${C.warn}`,
                  background: C.warnSoft,
                  color: C.warn,
                  cursor: 'pointer',
                }}
              >
                📦 Move to Backlog
              </button>
            )}
            {canDelete && onDeleteStory && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Delete story "${storyTitle}"?\n\nThis will permanently delete the story. Child tasks will remain but lose their story association.`)) {
                    onDeleteStory(story.issueid);
                    onClose();
                  }
                }}
                style={{
                  ...kanbanStoryActionBtnBase,
                  flexShrink: 0,
                  border: `1px solid ${C.dangerSoft}`,
                  background: C.dangerSoft,
                  color: C.danger,
                  cursor: 'pointer',
                }}
              >
                🗑 Delete
              </button>
            )}
            {story.projectid && canEditStoryEpic ? (
              <div
                style={{
                  flex: '1 1 200px',
                  minWidth: 160,
                  maxWidth: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <select
                  value={draftEpicParent}
                  onChange={(e) => setDraftEpicParent(e.target.value)}
                  disabled={epicsForStoryLoading}
                  aria-label="Epic (optional)"
                  title="Optional: link this story to a project epic"
                  style={{
                    ...kanbanStoryActionSelectBase,
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  <option value="">
                    {epicsForStoryLoading ? 'Loading epics…' : epicsForStory.length === 0 ? 'No epics in project' : 'Select Epic'}
                  </option>
                  {epicsForStory.map((ep) => {
                    const t = (ep.description || '').split('\n')[0].trim() || 'Epic';
                    const short = t.length > 48 ? `${t.slice(0, 48)}…` : t;
                    return (
                      <option key={ep.issueid} value={ep.issueid}>
                        #{ep.issueid} · {short}
                        {ep.epic_status ? ` (${ep.epic_status})` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            ) : (
              <div style={{ flex: '1 1 24px', minWidth: 8 }} aria-hidden />
            )}
          </div>
          {storyIsDirty && (
            <div
              style={{
                marginTop: '0.35rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: C.warn,
              }}
              aria-live="polite"
            >
              Unsaved changes
            </div>
          )}
        </div>
        {/* Editable Description */}
        <div style={{ marginBottom: '0.6rem' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: C.inkSoft, marginBottom: '4px' }}>Description</label>
          <textarea
            value={draftDescription}
            onChange={(e) => setDraftDescription(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '0.85rem',
              border: `1px solid ${C.line}`,
              borderRadius: 6,
              background: C.sunken,
              resize: 'vertical',
              minHeight: 60,
            }}
            placeholder="Details and acceptance criteria (title is edited above)…"
          />
        </div>

        {/* Links Section - View/Edit Toggle */}
        <div style={{ marginBottom: '0.6rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '4px' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: C.inkSoft }}>Links</span>
            <button
              onClick={() => setEditLinksMode(!editLinksMode)}
              style={{
                padding: '2px 8px',
                fontSize: '0.7rem',
                border: `1px solid ${C.line}`,
                borderRadius: 4,
                background: C.sunken,
                cursor: 'pointer',
                color: C.inkSoft,
              }}
              title={editLinksMode ? 'Done editing' : 'Edit links'}
            >
              {editLinksMode ? '✓ Done' : '✏️ Edit'}
            </button>
          </div>

          {/* Edit Mode: Textarea */}
          {editLinksMode ? (
            <textarea
              value={draftLinks}
              onChange={(e) => setDraftLinks(e.target.value)}
              rows={2}
              style={{
                width: '100%',
                padding: '6px 10px',
                fontSize: '0.8rem',
                border: `1px solid ${C.line}`,
                borderRadius: 6,
                background: C.sunken,
                resize: 'vertical',
              }}
              placeholder="https://docs.example.com, https://figma.com/file/..."
            />
          ) : (
            /* View Mode: Doc Link buttons */
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              {storyUrls.length > 0 ? (
                storyUrls.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: C.accentInk,
                      background: C.accentSoft,
                      border: `1px solid ${C.accentSoft}`,
                      borderRadius: 6,
                      textDecoration: 'none',
                    }}
                  >
                    📄 Doc Link {idx + 1}
                  </a>
                ))
              ) : (
                <span style={{ fontSize: '0.75rem', color: C.inkFaint, fontStyle: 'italic' }}>
                  No links added. Click Edit to add.
                </span>
              )}
            </div>
          )}
        </div>

        {/* Task Kanban Board */}
        {tasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 1rem', background: C.sunken, borderRadius: 10, border: `1.5px dashed ${C.upcoming}` }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>📋</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: C.ink, marginBottom: 4 }}>No tasks yet</div>
            <div style={{ fontSize: '0.85rem', color: C.inkSoft, marginBottom: 16 }}>Break this story into tasks and assign them to your team.</div>
            <button
              type="button"
              onClick={() => setShowAddTask(true)}
              title="Add the first task under this story"
              style={{ padding: '8px 20px', background: C.accentInk, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              + Add First Task
            </button>
          </div>
        ) : (
        // Already inside a modal — a carousel within a carousel is unusable on
        // a phone, so these columns simply stack.
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : `repeat(${visibleTaskColumns.length}, 1fr)`,
            gap: '0.75rem',
          }}
        >
          {visibleTaskColumns.map(col => {
            const colTasks = taskBoard[col] || [];
            return (
              <div key={col} style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.line}` }}>
                <div style={{ padding: '0.6rem 0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `3px solid ${TASK_COL_COLORS[col]}`, background: C.sunken }}>
                  <span style={{ fontWeight: 800, color: TASK_COL_COLORS[col], fontSize: '0.8rem' }}>{issueStatusLabel(col, boardDepartment)}</span>
                  <span style={{ background: `${TASK_COL_COLORS[col]}22`, color: TASK_COL_COLORS[col], borderRadius: 12, padding: '1px 6px', fontSize: '0.75rem', fontWeight: 700 }}>{colTasks.length}</span>
                </div>
                <div style={{ padding: '0.5rem', minHeight: 200 }}>
                  {colTasks.map(task => {
                    const p = PRIORITY_META[task.priority] || PRIORITY_META.Low;
                    return (
                      <div key={task.issueid} style={{ background: '#fff', borderRadius: 8, padding: '0.6rem', marginBottom: '0.5rem', border: `1px solid ${C.line}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', cursor: 'pointer' }} onClick={() => setSelectedTask(task)}>
                        <div style={{ fontSize: '0.65rem', color: C.inkFaint, marginBottom: 4 }}>
                          Task #{task.issueid}
                        </div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: C.ink, marginBottom: 6, lineHeight: 1.3 }}>
                          {task.description?.split('\n')[0] || 'Task'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: C.inkSoft, marginBottom: 6 }}>
                          {task.assignee_name || task.assignee_email || 'Unassigned'}
                          {task.story_points > 0 && (
                            <span style={{ marginLeft: 6, background: C.accentSoft, color: C.accent, padding: '1px 4px', borderRadius: 6, fontWeight: 700, fontSize: '0.65rem' }}>
                              {task.story_points}pts
                            </span>
                          )}
                        </div>
                        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                          <select 
                            value={task.assignee_id || ''}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              onReassign(task.issueid, e.target.value);
                            }}
                            style={{ width: '100%', fontSize: '0.7rem', padding: '3px 5px', borderRadius: 5, border: `1px solid ${C.upcoming}`, marginBottom: 4, cursor: 'pointer' }}>
                            <option value="">Assign...</option>
                            {assignableForTaskRow(task).map((u) => (
                              <option key={u.id} value={u.id}>{u.name || u.email}</option>
                            ))}
                          </select>
                          <select 
                            value={task.status}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              onStatusChange(task.issueid, e.target.value);
                            }}
                            style={{ width: '100%', fontSize: '0.7rem', padding: '3px 5px', borderRadius: 5, border: `1px solid ${C.upcoming}`, cursor: 'pointer' }}>
                            {workflowOptions.map(({ value, label }) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                            <option value="Closed">{issueStatusLabel('Done', boardDepartment)}</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        )}

      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onStatusChange={onStatusChange}
          onReassign={onReassign}
          users={users}
          onMoveToBacklog={
            selectedTask?.issuetype === 'Story' ? onMoveToBacklog : null
          }
          onMoveToStory={onMoveTaskToStory}
          availableStories={filterEpicsForSprintPicker(
            (allBoardIssues || []).filter(
              (i) => i.issuetype === 'Story' && String(i.issueid) !== String(selectedTask.issueid),
            ),
            story.sprint_id,
          )}
          parentIssue={(allBoardIssues || []).find((i) => String(i.issueid) === String(selectedTask.parent_id))}
          canEdit={canEditTaskDetail}
          canDelete={canDelete}
          onDeleteIssue={onDeleteTask || undefined}
        />
      )}

      {/* Add Task Form */}
      {showAddTask && (
        <div style={modalOverlay}>
          <div style={issueFormModalPanelStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: C.ink, fontWeight: 800 }}>Add Task to Story</h3>
              <button onClick={() => setShowAddTask(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
            </div>
            <IssueForm
              sprintId={sprintId}
              projectId={projectId}
              parentId={story.issueid}
              fixedType="Task"
              typeOptions={['Task', 'Story']}
              sprintList={[]}
              autoGrowDescription
              taskParentStory={{
                storyId: story.issueid,
                storyLabel:
                  (story.description || '').split('\n')[0]?.trim().slice(0, 100)
                  || `Story #${story.issueid}`,
                epic:
                  storyParentIssue?.issuetype === 'Epic'
                    ? {
                        id: storyParentIssue.issueid,
                        label:
                          (storyParentIssue.description || '').split('\n')[0]?.trim().slice(0, 100)
                          || `Epic #${storyParentIssue.issueid}`,
                        epic_status: storyParentIssue.epic_status,
                      }
                    : null,
              }}
              onSuccess={() => {
                setShowAddTask(false);
                onTaskCreated && onTaskCreated();
                toast.success('Task added!');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── New Project Modal ────────────────────────────────────────────────────────

const NewProjectModal = ({ onClose, onCreated, department = DEFAULT_DEPARTMENT }) => {
  const hints = useMemo(() => getDepartmentFormHints(department), [department]);
  const [form, setForm] = useState({
    projectname: '',
    status: 'Active',
    budgetallocated: '',
    startdate: '',
    enddate: '',
    department,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createProject({ ...form, department });
      toast.success('Project created!');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create project');
    }
    setSaving(false);
  };

  return (
    <div style={modalOverlay}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: C.ink, fontWeight: 800 }}>New Project</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Project Name *</label>
          <input style={inputStyle} value={form.projectname} onChange={e => setForm(f => ({ ...f, projectname: e.target.value }))}
            placeholder={hints.projectName} required />
          <label style={labelStyle}>Status</label>
          <select style={inputStyle} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            {['Planning', 'Active', 'On Hold', 'Completed'].map(s => <option key={s}>{s}</option>)}
          </select>
          <label style={labelStyle}>{hints.budgetLabel}</label>
          <input style={inputStyle} type="number" min={0} value={form.budgetallocated}
            onChange={e => setForm(f => ({ ...f, budgetallocated: e.target.value }))} placeholder={hints.budgetPlaceholder} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
            <div>
              <label style={labelStyle}>Start Date *</label>
              <input style={inputStyle} type="date" value={form.startdate}
                onChange={e => setForm(f => ({ ...f, startdate: e.target.value }))} required />
            </div>
            <div>
              <label style={labelStyle}>End Date *</label>
              <input style={inputStyle} type="date" value={form.enddate}
                onChange={e => setForm(f => ({ ...f, enddate: e.target.value }))} required />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
            <button type="button" onClick={onClose} style={btnStyle(C.inkFaint)}>Cancel</button>
            <button type="submit" disabled={saving} style={btnStyle(C.accentInk)}>
              {saving ? 'Creating…' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Create / Edit Sprint Modal ───────────────────────────────────────────────

const SprintFormModal = ({ projects, existingSprint, onClose, onSaved, onNavigateToAI, onDelete, department = DEFAULT_DEPARTMENT }) => {
  const hints = useMemo(() => getDepartmentFormHints(department), [department]);
  const isEdit = !!existingSprint;
  // Released / Closed sprints are intentionally rename-only: the backend
  // rejects any other field change, so the form should mirror that contract
  // instead of letting the user fill values that get silently discarded.
  const isRenameOnly = isEdit && ['Released', 'Closed'].includes(existingSprint?.status);
  const parsedExistingGoal = parseSprintGoal(existingSprint?.goal || '');
  const [form, setForm] = useState({
    project_id: existingSprint?.project_id || '',
    sprint_name: existingSprint?.sprint_name || '',
    goal: parsedExistingGoal.goal,
    brd_link: parsedExistingGoal.brdLink,
    figma_link: parsedExistingGoal.figmaLink,
    start_date: existingSprint?.start_date?.slice(0, 10) || '',
    end_date: existingSprint?.end_date?.slice(0, 10) || '',
  });
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  /**
   * Status is edited separately from the rest of the form because it goes to a
   * different endpoint (/status, not the sprint body) and carries its own
   * lifecycle rules. Kept here so a manager can reopen a completed sprint
   * without hunting for a board button that only exists in some states.
   */
  const [statusDraft, setStatusDraft] = useState(existingSprint?.status || '');
  /**
   * Expanded panel: epic title + AI drafts. Starts collapsed — an epic is
   * genuinely optional, and reference links no longer live in here.
   */
  const [showEpicOptions, setShowEpicOptions] = useState(false);
  const [aiPlanningEnabled, setAiPlanningEnabled] = useState(false);
  const [epicBrdFile, setEpicBrdFile] = useState(null);
  const [aiBreakdownMode, setAiBreakdownMode] = useState('generic');
  const [epicTitle, setEpicTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteIssuesOption, setDeleteIssuesOption] = useState(false);
  const submitLockRef = useRef(false);

  const handleFormChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleCloseAttempt = () => {
    if (isDirty && !saving) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (aiPlanningEnabled && !epicBrdFile) {
      toast.error('Add a BRD file for AI Smart Planning, or turn the toggle off.');
      return;
    }
    if (aiPlanningEnabled && !(epicTitle || '').trim()) {
      toast.error('Enter an epic title — it is used for the board and for AI planning.');
      return;
    }
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSaving(true);
    try {
      const { brd_link, figma_link, ...restForm } = form;
      const payload = {
        ...restForm,
        goal: composeSprintGoal(restForm.goal, normalizeUrl(brd_link), normalizeUrl(figma_link)),
      };

      let sprintId;
      if (isEdit) {
        // For Released / Closed sprints, only ship the rename — backend rejects
        // any other field change for those statuses.
        const editPayload = isRenameOnly ? { sprint_name: payload.sprint_name } : payload;
        await editSprint(existingSprint.id, editPayload);
        sprintId = existingSprint.id;
        // Status travels on its own endpoint, and only when it actually
        // changed — sending it every save would churn the sprint's history.
        if (statusDraft && statusDraft !== existingSprint.status) {
          await overrideSprintStatus(existingSprint.id, statusDraft);
        }
        toast.success('Sprint updated!');
        if (isRenameOnly) {
          // Released / Closed sprints can only be renamed — skip the epic / AI
          // pipeline entirely so we don't touch any historical work.
          onSaved(sprintId, { isNew: false, projectId: existingSprint.project_id });
          onClose();
          return;
        }
      } else {
        const res = await createSprint(payload);
        sprintId = res.data.id;
        toast.success('Sprint created!');
      }

      const epicNameTrim = (epicTitle || '').trim();

      // Always create the Epic if a title was provided (with or without AI planning)
      let epicIssueId = null;
      if (epicNameTrim && sprintId) {
        try {
          const { createIssue } = await import('../api/issueApi');
          const boardRes = await getSprintBoard(sprintId);
          const rows = boardRes.data?.allIssues || [];
          const existingEpic = rows.find(
            (row) =>
              row.issuetype === 'Epic' &&
              (row.parent_id == null || row.parent_id === '') &&
              (((row.description || '').split('\n')[0] || row.description || '').trim() === epicNameTrim)
          );
          if (existingEpic) {
            epicIssueId = existingEpic.issueid;
            toast.success('Using existing epic with the same title.');
          } else {
            const epicRes = await createIssue({
              projectid: restForm.project_id,
              sprint_id: sprintId,
              issuetype: 'Epic',
              description: epicNameTrim,
              assigneeteam: 'Product',
              priority: 'High',
            });
            epicIssueId = epicRes.data.issue.issueid;
            toast.success(`Epic "${epicNameTrim}" created!`);
          }
        } catch (epicErr) {
          console.error('Epic creation error:', epicErr);
          toast.error(epicErr.response?.data?.message || 'Failed to create epic');
        }
      }

      // Run AI planning only if enabled AND a BRD file was provided
      if (aiPlanningEnabled && epicBrdFile && sprintId && epicIssueId) {
        setUploading(true);
        try {
          const { uploadBRD, generateTasksFromBRD } = await import('../api/aiApi');
          const uploadRes = await uploadBRD(epicBrdFile, sprintId, epicIssueId);
          toast.success('Epic BRD uploaded!');
          const projectName = projects.find(p => String(p.projectid) === String(restForm.project_id))?.projectname;
          await generateTasksFromBRD({
            documentId: uploadRes.data.document.id,
            sprintId,
            sprintGoal: form.goal,
            projectName,
            epicIssueId,
            epicTitle: epicNameTrim,
            breakdownMode: aiBreakdownMode,
          });
          toast.success('AI generated drafts for this epic — review and approve.');
          if (onNavigateToAI) {
            onNavigateToAI(sprintId, epicIssueId);
          }
        } catch (aiErr) {
          console.error('Epic AI error:', aiErr);
          toast.error(aiErr.response?.data?.message || 'Epic BRD / AI generation failed');
        } finally {
          setUploading(false);
        }
      }

      onSaved(sprintId, { isNew: !isEdit, projectId: restForm.project_id });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} sprint`);
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  const acceptBrdFile = (file) => {
    if (!file) return;
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const validExts = ['.pdf', '.docx', '.txt'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(file.type) && !validExts.includes(ext)) {
      toast.error('Please upload a PDF, Word document, or text file');
      return;
    }
    return file;
  };

  const handleEpicBrdFileChange = (e) => {
    const file = acceptBrdFile(e.target.files?.[0]);
    if (file) {
      setIsDirty(true);
      setEpicBrdFile(file);
    }
  };

  const lockedInputStyle = { ...inputStyle, background: C.sunken, color: C.inkSoft, cursor: 'not-allowed' };

  return (
    <div style={modalOverlay}>
      <div style={{ ...modalBox, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: C.ink, fontWeight: 800 }}>
            {isEdit ? (isRenameOnly ? '✏️ Rename Sprint' : '✏️ Edit Sprint') : '⚡ New Sprint'}
          </h3>
          <button onClick={handleCloseAttempt} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
        </div>
        {isRenameOnly && (
          <div style={{
            background: C.warnSoft,
            border: `1px solid ${C.warnSoft}`,
            color: C.warn,
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 14,
            fontSize: '0.82rem',
            lineHeight: 1.5,
          }}>
            <strong>{existingSprint?.status} sprint:</strong> only the title can be edited here. Description, dates, reference links and epic / AI options are locked to preserve the historical record.
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
            <div>
              <label style={{ ...labelStyle, marginTop: 0 }}>Project *</label>
              <select style={isRenameOnly ? lockedInputStyle : inputStyle} value={form.project_id}
                onChange={e => handleFormChange('project_id', e.target.value)}
                disabled={isEdit} required>
                <option value="">Select project…</option>
                {projects.map(p => <option key={p.projectid} value={p.projectid}>{p.projectname}</option>)}
              </select>
            </div>
            <div>
              <label style={{ ...labelStyle, marginTop: 0 }}>Sprint name *</label>
              <input style={inputStyle} value={form.sprint_name}
                onChange={e => handleFormChange('sprint_name', e.target.value)}
                placeholder={hints.sprintName} required />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
            <div>
              <label style={labelStyle}>Start Date *</label>
              <input
                style={isRenameOnly ? lockedInputStyle : inputStyle}
                type="date"
                value={form.start_date}
                onChange={e => handleFormChange('start_date', e.target.value)}
                required={!isRenameOnly}
                disabled={isRenameOnly}
              />
            </div>
            <div>
              <label style={labelStyle}>End Date *</label>
              <input
                style={isRenameOnly ? lockedInputStyle : inputStyle}
                type="date"
                value={form.end_date}
                onChange={e => handleFormChange('end_date', e.target.value)}
                required={!isRenameOnly}
                disabled={isRenameOnly}
              />
            </div>
          </div>

          {/*
            Editable even for Released / Closed sprints, which are otherwise
            rename-only: reopening a sprint that was finished too early is the
            whole reason this control exists, and the status endpoint is a
            different call from the rename the lock applies to.
          */}
          {isEdit && (
            <>
              <label style={labelStyle}>Status</label>
              <select
                style={inputStyle}
                value={statusDraft}
                onChange={e => { setStatusDraft(e.target.value); setIsDirty(true); }}
              >
                {SPRINT_STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {statusDraft !== existingSprint?.status && (
                <div style={{
                  fontSize: '0.74rem',
                  marginTop: -4,
                  marginBottom: 10,
                  color: REOPENS_RELEASED(existingSprint?.status, statusDraft) ? C.warn : C.inkSoft,
                  fontWeight: REOPENS_RELEASED(existingSprint?.status, statusDraft) ? 700 : 500,
                }}>
                  {REOPENS_RELEASED(existingSprint?.status, statusDraft)
                    ? `⚠️ ${existingSprint.status} → ${statusDraft}. This sprint already has a release record, which will still point at it while it runs again.`
                    : `${existingSprint?.status} → ${statusDraft} on save.${statusDraft === 'Active' ? ' Any other active sprint in this project moves to Review.' : ''}`}
                </div>
              )}
            </>
          )}

          <label style={labelStyle}>Description</label>
          <textarea
            style={{ ...(isRenameOnly ? lockedInputStyle : inputStyle), resize: 'vertical' }}
            rows={2}
            value={form.goal}
            onChange={e => handleFormChange('goal', e.target.value)}
            placeholder={hints.sprintGoal}
            disabled={isRenameOnly}
          />

          {/*
            Reference links belong to the SPRINT, not the epic — they are stored
            in the sprint's own goal field and render on the board header with no
            epic involved. They used to sit inside the epic panel, which made a
            release without an epic look like it could not carry links.
          */}
          {!isRenameOnly && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
              <SprintGoalLinkInput
                label="BRD Link (Optional)"
                value={form.brd_link}
                onChange={(e) => handleFormChange('brd_link', e.target.value)}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
              />
              <SprintGoalLinkInput
                label="Figma Link (Optional)"
                value={form.figma_link}
                onChange={(e) => handleFormChange('figma_link', e.target.value)}
                inputStyle={inputStyle}
                labelStyle={labelStyle}
              />
            </div>
          )}

          {!isRenameOnly && (
            <button
              type="button"
              onClick={() => {
                setIsDirty(true);
                setShowEpicOptions((v) => {
                  if (v) {
                    // Collapsing means "no epic" — drop the title too, or a
                    // hidden value would still create one on submit.
                    setEpicTitle('');
                    setAiPlanningEnabled(false);
                    setEpicBrdFile(null);
                    setAiBreakdownMode('generic');
                  }
                  return !v;
                });
              }}
              style={{
                marginTop: 16,
                width: '100%',
                padding: '0.65rem 1rem',
                borderRadius: 10,
                border: `1.5px solid ${showEpicOptions ? C.accent : C.upcoming}`,
                background: showEpicOptions ? C.accentSoft : '#fff',
                color: C.accentInk,
                fontWeight: 700,
                fontSize: '0.88rem',
                cursor: 'pointer',
              }}
            >
              {showEpicOptions ? '▼ Hide epic section' : '+ Add an epic (optional)'}
            </button>
          )}
          {isEdit && !isRenameOnly && (
            <p style={{ margin: '8px 0 0', fontSize: '0.72rem', color: C.inkSoft, lineHeight: 1.45 }}>
              Add another epic to this sprint, or run AI planning for it.
            </p>
          )}

          {!isRenameOnly && showEpicOptions && (
            <div style={{
              marginTop: 12,
              padding: '14px 16px',
              background: C.sunken,
              border: `1px solid ${C.line}`,
              borderRadius: 12,
            }}>
              <label style={{ ...labelStyle, marginTop: 0 }}>Epic title</label>
              <input
                style={inputStyle}
                value={epicTitle}
                onChange={(e) => { setIsDirty(true); setEpicTitle(e.target.value); }}
                placeholder="Short epic name for the board (e.g. Group travel & invites)"
              />
              <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: C.inkSoft }}>
                Leave blank to create the sprint without an epic.
              </p>

              <div style={{
                marginTop: 14, padding: '12px 14px',
                background: aiPlanningEnabled ? C.doneSoft : '#fff',
                border: `1px solid ${aiPlanningEnabled ? C.done : C.line}`,
                borderRadius: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: aiPlanningEnabled ? 12 : 0 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: C.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
                      🤖 AI Smart Planning
                    </div>
                    <div style={{ fontSize: '0.75rem', color: C.inkSoft, marginTop: 2 }}>
                      Creates an Epic, uploads your BRD, drafts stories and tasks under that epic — then review and approve
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsDirty(true);
                      const next = !aiPlanningEnabled;
                      setAiPlanningEnabled(next);
                      if (!next) {
                        setEpicBrdFile(null);
                        setAiBreakdownMode('generic');
                      }
                    }}
                    style={{
                      width: 48, height: 26, borderRadius: 13, border: 'none',
                      background: aiPlanningEnabled ? C.done : C.upcoming,
                      cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 3, left: aiPlanningEnabled ? 25 : 3,
                      width: 20, height: 20, borderRadius: '50%', background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                    }} />
                  </button>
                </div>

                {aiPlanningEnabled && (
                  <div>
                    <AiBreakdownModePicker groupName="sprint-form-ai-breakdown" value={aiBreakdownMode} onChange={(v) => { setIsDirty(true); setAiBreakdownMode(v); }} disabled={uploading} />
                    <label style={{ ...labelStyle, marginTop: 0 }}>Upload BRD document *</label>
                    <div
                      style={{
                        border: `2px dashed ${C.upcoming}`, borderRadius: 8, padding: '14px',
                        textAlign: 'center', background: '#fff', cursor: 'pointer',
                      }}
                      onClick={() => document.getElementById('sprint-epic-brd-input').click()}
                    >
                      <input
                        id="sprint-epic-brd-input"
                        type="file"
                        accept=".pdf,.docx,.txt"
                        onChange={handleEpicBrdFileChange}
                        style={{ display: 'none' }}
                      />
                      {epicBrdFile ? (
                        <div>
                          <div style={{ fontWeight: 600, color: C.ink }}>{epicBrdFile.name}</div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setIsDirty(true); setEpicBrdFile(null); }}
                            style={{ marginTop: 8, fontSize: '0.75rem', color: C.danger, background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            Remove file
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontWeight: 600, color: C.inkSoft, fontSize: '0.85rem' }}>Click to upload BRD</div>
                          <div style={{ fontSize: '0.72rem', color: C.inkFaint, marginTop: 4 }}>PDF, Word, or Text</div>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 12px', background: C.warnSoft, borderRadius: 6, fontSize: '0.78rem', color: C.warn }}>
                      After save you will go to AI draft review to approve stories and tasks linked to this epic.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            {isEdit && (
              <button type="button" 
                onClick={() => setShowDeleteConfirm(true)}
                style={{ padding: '0.7rem 1rem', border: `1px solid ${C.dangerSoft}`, borderRadius: 8, background: C.dangerSoft, color: C.danger, cursor: 'pointer', fontWeight: 600 }}>
                🗑️ Delete
              </button>
            )}
            <button type="button" onClick={handleCloseAttempt}
              style={{ flex: 1, padding: '0.7rem', border: `1px solid ${C.line}`, borderRadius: 8, background: C.sunken, cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
            <button type="submit" disabled={saving || uploading}
              style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: 8, background: C.accentInk, color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
              {saving || uploading ? 'Processing…' : (isEdit ? 'Save Changes' : 'Create Sprint')}
            </button>
          </div>
        </form>

        {/* Unsaved Changes Confirmation Dialog */}
        {showConfirmClose && (
          <div style={{ ...modalOverlay, zIndex: 10000 }}>
            <div style={{ ...modalBox, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: 0, color: C.warn, fontWeight: 800, marginBottom: 12 }}>
                ⚠️ Unsaved Changes
              </h3>
              <p style={{ margin: 0, color: C.inkSoft, fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 20 }}>
                You have unsaved changes in this sprint form. Closing now will discard all your work.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowConfirmClose(false)}
                  style={{ flex: 1, padding: '0.7rem', border: `1px solid ${C.line}`, borderRadius: 8, background: C.sunken, cursor: 'pointer', fontWeight: 600 }}>
                  Keep Editing
                </button>
                <button onClick={confirmClose}
                  style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: 8, background: C.danger, color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                  Discard & Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div style={{ ...modalOverlay, zIndex: 10000 }}>
            <div style={{ ...modalBox, maxWidth: 450 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: 0, color: C.danger, fontWeight: 800, marginBottom: 12 }}>
                ⚠️ Delete Sprint?
              </h3>
              <p style={{ margin: 0, color: C.inkSoft, fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 16 }}>
                Are you sure you want to delete <strong>{existingSprint?.sprint_name}</strong>?
              </p>
              
              {/* Checkbox for deleting issues */}
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                padding: '12px', 
                background: C.dangerSoft, 
                border: `1px solid ${C.dangerSoft}`, 
                borderRadius: 8,
                cursor: 'pointer',
                marginBottom: 16
              }}>
                <input 
                  type="checkbox" 
                  checked={deleteIssuesOption}
                  onChange={(e) => setDeleteIssuesOption(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: C.danger, fontSize: '0.85rem' }}>
                    🗑️ Also delete all Epics, Stories and Tasks
                  </div>
                  <div style={{ fontSize: '0.75rem', color: C.danger, marginTop: 2 }}>
                    {deleteIssuesOption
                      ? 'Epics, stories and tasks will be permanently deleted'
                      : 'Epics, stories and tasks will be moved to backlog'}
                  </div>
                </div>
              </label>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setShowDeleteConfirm(false); setDeleteIssuesOption(false); }}
                  style={{ flex: 1, padding: '0.7rem', border: `1px solid ${C.line}`, borderRadius: 8, background: C.sunken, cursor: 'pointer', fontWeight: 600 }}>
                  Cancel
                </button>
                <button onClick={() => { onDelete(existingSprint.id, deleteIssuesOption); setShowDeleteConfirm(false); setDeleteIssuesOption(false); }}
                  style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: 8, background: C.danger, color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                  Delete Sprint
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Financial Year helpers ───────────────────────────────────────────────────

// India FY: Apr 1 – Mar 31
const getFY = (date) => {
  const d = new Date(date);
  const m = d.getMonth(); // 0-indexed
  return m >= 3 ? d.getFullYear() : d.getFullYear() - 1; // Apr(3) onwards = current year
};

const fyLabel = (fy) => `FY ${fy}-${String(fy + 1).slice(2)}`;

const fyRange = (fy) => ({
  start: new Date(`${fy}-04-01`),
  end:   new Date(`${fy + 1}-03-31T23:59:59`),
});

const currentFY = getFY(new Date());

const LAST_SELECTED_PROJECT_KEY = 'sprintBoard:lastSelectedProject';
const LAST_SELECTED_SPRINT_MAP_KEY = 'sprintBoard:lastSelectedSprintByProject';
const ALL_PROJECTS_CACHE_KEY = '__all__';

const getProjectCacheKey = (projectId) =>
  projectId === null || projectId === undefined || projectId === ''
    ? ALL_PROJECTS_CACHE_KEY
    : String(projectId);

const readStoredSprintMap = () => {
  try {
    const raw = localStorage.getItem(LAST_SELECTED_SPRINT_MAP_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/** Most-recent-first project ids for Sprint Board carousel ordering (per browser). */
const RECENT_PROJECTS_KEY = 'sprintBoard:recentProjectIds';
const MAX_RECENT_PROJECTS = 50;

const readRecentProjectIdsFromStorage = () => {
  try {
    const raw = localStorage.getItem(RECENT_PROJECTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((x) => String(x)).filter((id) => id && id !== 'null');
  } catch {
    return [];
  }
};

/** Persist recent project for the *next* visit only — does not update React state (stable sort per session). */
const bumpRecentProjectInLocalStorageOnly = (projectId) => {
  const id = String(projectId);
  if (!id || id === 'null') return;
  try {
    const prev = readRecentProjectIdsFromStorage();
    const next = [id, ...prev.filter((x) => String(x) !== id)].slice(0, MAX_RECENT_PROJECTS);
    localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
};

// ─── Main Sprint Board ────────────────────────────────────────────────────────

const SprintBoard = () => {
  const { role, user } = useContext(AuthContext);
  const isMobile = useIsMobile();
  const boardDepartment = user?.department || DEFAULT_DEPARTMENT;
  const departmentView = getStoredDepartmentView();
  const effectiveBoardDepartment = departmentView || boardDepartment;
  const { departmentUsesSprints, getWorkBoardLabel, getCycleLabel } = useDepartmentWorkflow();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const navigateToAiPlanning = useCallback((sid, epicIssueId) => {
    if (sid == null || sid === '') return;
    const q = epicIssueId != null && epicIssueId !== ''
      ? `?epicId=${encodeURIComponent(String(epicIssueId))}`
      : '';
    navigate(`/ai-planning/${sid}${q}`);
  }, [navigate]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNotebookModal, setShowNotebookModal] = useState(
    () => searchParams.get('tab') === 'notebook'
  );

  useEffect(() => {
    if (searchParams.get('tab') === 'notebook') {
      setShowNotebookModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /** Deep-link from Projects Overview (and similar): /sprint-board?projectId=&sprintId= */
  useEffect(() => {
    const pid = searchParams.get('projectId');
    const sid = searchParams.get('sprintId');
    if (!pid) return;
    setSelectedProject(String(pid));
    if (sid) setSelectedSprint(String(sid));
    const next = new URLSearchParams(searchParams);
    next.delete('projectId');
    next.delete('sprintId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const isAdmin = isSprintSteward(role);
  const canDeleteItems = canDeleteWorkItems(role);
  const canChangeEpicStatus = isSprintSteward(role);

  // Load last selected project/sprint from localStorage
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem(LAST_SELECTED_PROJECT_KEY) ?? localStorage.getItem('lastSelectedProject');
    return saved && saved !== 'null' ? saved : null;
  });
  const [selectedSprint, setSelectedSprint]   = useState(() => {
    const savedProject = localStorage.getItem(LAST_SELECTED_PROJECT_KEY) ?? localStorage.getItem('lastSelectedProject');
    const sprintMap = readStoredSprintMap();
    const mappedSprint = sprintMap[getProjectCacheKey(savedProject && savedProject !== 'null' ? savedProject : null)];
    const legacySprint = localStorage.getItem('lastSelectedSprint');
    const selected = mappedSprint || legacySprint;
    return selected && selected !== 'null' ? String(selected) : null;
  });
  const [selectedFY, setSelectedFY]           = useState(null); // null = All years
  const [showNewProject, setShowNewProject]   = useState(false);
  const [showSprintForm, setShowSprintForm]   = useState(false);
  const [editingSprint, setEditingSprint]     = useState(null);
  const [showRelease, setShowRelease]         = useState(false);
  const [showIssueForm, setShowIssueForm]     = useState(false);
  const [showBacklog, setShowBacklog]         = useState(false);
  const [showBacklogForm, setShowBacklogForm] = useState(false); // Add task directly to backlog
  const [releaseData, setReleaseData]         = useState({ version: '', release_notes: '' });
  const [confirmDialog, setConfirmDialog]     = useState(null); // { title, message, warning, onConfirm }
  const [taskModal, setTaskModal]             = useState(null); // { story, tasks }
  const [selectedDirectTask, setSelectedDirectTask] = useState(null); // Task clicked directly from board
  /** Story to restore when user backs out of a child task detail view. */
  const [storyDetailReturn, setStoryDetailReturn] = useState(null);
  const [selectedEpicId, setSelectedEpicId]   = useState(null);
  const [epicDetailModalId, setEpicDetailModalId] = useState(null);
  const [recentProjectIds, setRecentProjectIds] = useState(readRecentProjectIdsFromStorage);

  // Save selections to localStorage
  useEffect(() => {
    localStorage.setItem(LAST_SELECTED_PROJECT_KEY, selectedProject ?? 'null');
    localStorage.setItem('lastSelectedProject', selectedProject ?? 'null');
  }, [selectedProject]);

  useEffect(() => {
    const sprintMap = readStoredSprintMap();
    const projectKey = getProjectCacheKey(selectedProject);

    if (selectedSprint) {
      sprintMap[projectKey] = String(selectedSprint);
      localStorage.setItem('lastSelectedSprint', String(selectedSprint));
    } else {
      delete sprintMap[projectKey];
      localStorage.setItem('lastSelectedSprint', 'null');
    }

    localStorage.setItem(LAST_SELECTED_SPRINT_MAP_KEY, JSON.stringify(sprintMap));
  }, [selectedSprint, selectedProject]);

  const handleProjectSelect = (projectId) => {
    const normalizedProjectId = projectId === null || projectId === undefined ? '' : String(projectId);
    if (normalizedProjectId) {
      bumpRecentProjectInLocalStorageOnly(normalizedProjectId);
    }
    const sprintMap = readStoredSprintMap();
    const cachedSprint = sprintMap[getProjectCacheKey(normalizedProjectId)];

    setSelectedProject(normalizedProjectId);
    setSelectedSprint(cachedSprint ? String(cachedSprint) : null);
  };

  // ── Data queries ──
  // Use getProjects (/projects/all) — backend returns all non-archived projects
  // to all authenticated roles (seeAllProjects = true in getAllProjects controller).
  const { data: projects = [], isFetched: isProjectsFetched } = useQuery({
    queryKey: ['projects', role, departmentView || 'all'],
    queryFn: () => getProjects().then(r => r.data),
  });

  /** True when selected project belongs to the current department-scoped project list. */
  const isSelectedProjectInScope = useMemo(() => {
    if (!isProjectsFetched || projects.length === 0) return false;
    if (selectedProject === null || selectedProject === undefined || selectedProject === '') {
      return isAdmin;
    }
    return projects.some((p) => String(p.projectid) === String(selectedProject));
  }, [isProjectsFetched, projects, selectedProject, isAdmin]);

  useEffect(() => {
    if (!isProjectsFetched || projects.length === 0) return;
    const allowed = new Set(projects.map((p) => String(p.projectid)));
    setRecentProjectIds((prev) => {
      const next = prev.filter((id) => allowed.has(String(id)));
      if (next.length === prev.length) return prev;
      try {
        localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [isProjectsFetched, projects]);

  const { data: sprints = [], isFetched: isSprintsFetched } = useQuery({
    queryKey: ['sprints', selectedProject, departmentView || 'all'],
    queryFn: () => getSprints(selectedProject || null).then(r => r.data),
    enabled: isSelectedProjectInScope,
  });

  // All sprints (no project filter) — used for project card sprint counts
  const { data: allSprints = [] } = useQuery({
    queryKey: ['sprints', 'all', departmentView || 'all'],
    queryFn: () => getSprints(null).then(r => r.data),
    enabled: isProjectsFetched && (isAdmin || projects.length > 0),
  });

  const sprintsInScope = isSelectedProjectInScope ? sprints : [];

  /** Active sprint first, then most recently opened, then API order. */
  const orderedProjects = useMemo(() => {
    if (!projects.length) return projects;
    const originalIndex = new Map(projects.map((p, i) => [String(p.projectid), i]));
    const recentRank = new Map(recentProjectIds.map((id, idx) => [String(id), idx]));
    const hasActiveSprint = (projectId) =>
      allSprints.some(
        (s) =>
          String(s.project_id ?? s.projectid ?? '').trim() === String(projectId).trim() &&
          s.status === 'Active'
      );
    const INACTIVE_RANK = 9999;

    return [...projects].sort((a, b) => {
      const aid = String(a.projectid);
      const bid = String(b.projectid);
      const scoreA = hasActiveSprint(aid) ? 1 : 0;
      const scoreB = hasActiveSprint(bid) ? 1 : 0;
      if (scoreB !== scoreA) return scoreB - scoreA;
      const ra = recentRank.has(aid) ? recentRank.get(aid) : INACTIVE_RANK;
      const rb = recentRank.has(bid) ? recentRank.get(bid) : INACTIVE_RANK;
      if (ra !== rb) return ra - rb;
      return (originalIndex.get(aid) ?? 0) - (originalIndex.get(bid) ?? 0);
    });
  }, [projects, allSprints, recentProjectIds]);

  useEffect(() => {
    if (!selectedSprint || !isSprintsFetched || !isSelectedProjectInScope) return;
    const exists = sprintsInScope.some(s => String(s.id) === String(selectedSprint));
    if (!exists) {
      setSelectedSprint(null);
    }
  }, [sprintsInScope, selectedSprint, isSprintsFetched, isSelectedProjectInScope]);

  // Reset cached project/sprint when selection is outside the current department project list
  useEffect(() => {
    if (!isProjectsFetched) return;

    if (projects.length === 0) {
      if (selectedProject || selectedSprint) {
        setSelectedProject(isAdmin ? '' : null);
        setSelectedSprint(null);
      }
      return;
    }

    if (
      selectedProject &&
      selectedProject !== '' &&
      !projects.some((p) => String(p.projectid) === String(selectedProject))
    ) {
      if (isAdmin) {
        setSelectedProject('');
        setSelectedSprint(null);
      } else {
        const first = orderedProjects[0];
        if (!first) {
          setSelectedProject(null);
          setSelectedSprint(null);
          return;
        }
        bumpRecentProjectInLocalStorageOnly(String(first.projectid));
        setSelectedProject(String(first.projectid));
        setSelectedSprint(null);
      }
    }
  }, [isProjectsFetched, projects, isAdmin, selectedProject, selectedSprint, orderedProjects]);

  const boardQueryKey = selectedSprint
    ? ['board', 'sprint', String(selectedSprint)]
    : selectedProject
      ? ['board', 'backlog', String(selectedProject)]
      : ['board', 'none'];

  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: boardQueryKey,
    queryFn: () =>
      selectedSprint
        ? getSprintBoard(selectedSprint).then((r) => r.data)
        : getProjectBacklogBoard(selectedProject).then((r) => r.data),
    enabled: isSelectedProjectInScope && !!(selectedSprint || selectedProject),
  });

  const isBacklogBoardView = Boolean(boardData?.backlog);

  useEffect(() => {
    if (!selectedDirectTask || !boardData?.allIssues) return;
    const fresh = boardData.allIssues.find(
      (i) => String(i.issueid) === String(selectedDirectTask.issueid),
    );
    if (!fresh) return;
    const snap = `${fresh.status}|${fresh.assignee_id ?? ''}|${fresh.priority ?? ''}|${fresh.story_points ?? ''}`;
    const cur = `${selectedDirectTask.status}|${selectedDirectTask.assignee_id ?? ''}|${selectedDirectTask.priority ?? ''}|${selectedDirectTask.story_points ?? ''}`;
    if (snap !== cur) setSelectedDirectTask(fresh);
  }, [boardData?.allIssues, selectedDirectTask?.issueid, selectedDirectTask?.status, selectedDirectTask?.assignee_id, selectedDirectTask?.priority, selectedDirectTask?.story_points]);

  useEffect(() => {
    if (!storyDetailReturn || !boardData?.allIssues) return;
    const fresh = boardData.allIssues.find(
      (i) => String(i.issueid) === String(storyDetailReturn.issueid),
    );
    if (!fresh) return;
    const snap = `${fresh.status}|${fresh.assignee_id ?? ''}|${fresh.priority ?? ''}|${fresh.story_points ?? ''}`;
    const cur = `${storyDetailReturn.status}|${storyDetailReturn.assignee_id ?? ''}|${storyDetailReturn.priority ?? ''}|${storyDetailReturn.story_points ?? ''}`;
    if (snap !== cur) setStoryDetailReturn(fresh);
  }, [boardData?.allIssues, storyDetailReturn?.issueid, storyDetailReturn?.status, storyDetailReturn?.assignee_id, storyDetailReturn?.priority, storyDetailReturn?.story_points]);

  useEffect(() => {
    setSelectedEpicId(null);
    setEpicDetailModalId(null);
  }, [selectedSprint, selectedProject]);

  const issueById = useMemo(() => {
    const m = new Map();
    (boardData?.allIssues || []).forEach((row) => {
      m.set(String(row.issueid), row);
    });
    return m;
  }, [boardData]);

  const displayBoard = useMemo(() => {
    const empty = {};
    BOARD_COLUMNS.forEach((c) => { empty[c] = []; });
    if (!boardData?.board) return empty;
    const pick = (issue) => {
      if (issue.issuetype === 'Bug') return false; // Bugs belong in Issues section only
      const rootEpic = rootEpicIdForIssue(issue, issueById);
      if (selectedEpicId) {
        return rootEpic === String(selectedEpicId);
      }
      if (rootEpic) return false;
      return true;
    };
    const out = { ...empty };
    BOARD_COLUMNS.forEach((col) => {
      out[col] = (boardData.board[col] || []).filter(pick);
    });
    return out;
  }, [boardData, selectedEpicId, issueById]);

  /** Hide Blocked when empty so other columns get more width; show as soon as any card is Blocked. */
  const visibleBoardColumns = useMemo(() => {
    if ((displayBoard.Blocked || []).length > 0) return BOARD_COLUMNS;
    return BOARD_COLUMNS.filter((c) => c !== 'Blocked');
  }, [displayBoard]);

  const epicBoardHasNoCards = useMemo(() => {
    if (!selectedEpicId) return false;
    return BOARD_COLUMNS.every((col) => (displayBoard[col] || []).length === 0);
  }, [displayBoard, selectedEpicId]);

  const selectedEpic = useMemo(() => {
    if (!selectedEpicId || !boardData?.allIssues) return null;
    return boardData.allIssues.find((i) => String(i.issueid) === String(selectedEpicId)) || null;
  }, [boardData, selectedEpicId]);

  useEffect(() => {
    const openIssueId = searchParams.get('openIssueId');
    if (!openIssueId || !boardData?.allIssues?.length) return;

    const clearParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('openIssueId');
      setSearchParams(next, { replace: true });
    };

    const target = boardData.allIssues.find(i => String(i.issueid) === String(openIssueId));
    if (!target) {
      toast('Notification issue is not on the current board');
      clearParam();
      return;
    }

    if (target.issuetype === 'Bug') {
      navigate(`/issues?openIssueId=${target.issueid}`);
      clearParam();
      return;
    }

    const storyId = target.issuetype === 'Story' ? target.issueid : target.parent_id;
    if (!storyId) {
      clearParam();
      return;
    }

    const story = boardData.allIssues.find(i => String(i.issueid) === String(storyId));
    const childTasks = boardData.allIssues.filter(i => String(i.parent_id) === String(storyId));
    if (story) {
      setSelectedDirectTask(story);
      setTaskModal(null);
    }

    clearParam();
  }, [boardData, navigate, searchParams, setSearchParams]);

  const { data: backlogPayload, isLoading: backlogLoading } = useQuery({
    queryKey: ['backlog', selectedProject],
    queryFn: () => getBacklog(selectedProject || null).then((r) => r.data),
    enabled: !!selectedProject && isAdmin,
    staleTime: 20_000,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users-visible', selectedProject],
    queryFn: () => getVisibleUsers(selectedProject || undefined).then((r) => r.data || []),
  });

  const userListForAssign = useMemo(() => {
    const raw = users;
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    return raw.data || [];
  }, [users]);

  const { data: customTeamsBoard = [] } = useQuery({
    queryKey: ['custom-teams'],
    queryFn: getCustomTeams,
  });
  const boardTeamsMeta = useMemo(
    () => mergeTeamsForDepartment(customTeamsBoard, boardDepartment),
    [customTeamsBoard, boardDepartment],
  );

  // ── Mutations ──
  const invalidateSprints = () => qc.invalidateQueries({ queryKey: ['sprints', selectedProject] });
  const invalidateBoard = () => qc.invalidateQueries({ queryKey: ['board'] });

  const reorderEpicStoriesInColumn = useCallback(
    async (columnLabel, issueId, direction) => {
      if (columnLabel !== 'Open' || !isAdmin) return;
      const colIssues = displayBoard[columnLabel] || [];
      const storyList = colIssues.filter(
        (i) => i.issuetype === 'Story' && issueInBoardEpicScope(i, issueById, selectedEpicId),
      );
      const idx = storyList.findIndex((s) => String(s.issueid) === String(issueId));
      if (idx < 0) return;
      const j = direction === 'up' ? idx - 1 : idx + 1;
      if (j < 0 || j >= storyList.length) return;
      const reordered = [...storyList];
      const tmp = reordered[idx];
      reordered[idx] = reordered[j];
      reordered[j] = tmp;
      try {
        await Promise.all(
          reordered.map((s, i) => updateIssue(s.issueid, { stack_rank: (i + 1) * 10 })),
        );
        invalidateBoard();
        toast.success('Story order saved');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to reorder stories');
      }
    },
    [selectedEpicId, isAdmin, displayBoard, issueById, invalidateBoard],
  );

  /** Same epic + same column + same parent (story or epic): reorder sibling tasks (manager roles only). */
  const reorderEpicTasksInColumn = useCallback(
    async (columnLabel, issueId, direction) => {
      if (columnLabel !== 'Open' || !isAdmin) return;
      const colIssues = displayBoard[columnLabel] || [];
      const row = colIssues.find((i) => String(i.issueid) === String(issueId));
      if (!row || row.issuetype !== 'Task' || row.parent_id == null || row.parent_id === '') return;
      const parentKey = String(row.parent_id);
      const taskList = colIssues.filter(
        (i) =>
          i.issuetype === 'Task' &&
          i.parent_id != null &&
          i.parent_id !== '' &&
          String(i.parent_id) === parentKey &&
          issueInBoardEpicScope(i, issueById, selectedEpicId),
      );
      const idx = taskList.findIndex((t) => String(t.issueid) === String(issueId));
      if (idx < 0) return;
      const j = direction === 'up' ? idx - 1 : idx + 1;
      if (j < 0 || j >= taskList.length) return;
      const reordered = [...taskList];
      const tmp = reordered[idx];
      reordered[idx] = reordered[j];
      reordered[j] = tmp;
      try {
        await Promise.all(
          reordered.map((t, i) => updateIssue(t.issueid, { stack_rank: (i + 1) * 10 })),
        );
        invalidateBoard();
        toast.success('Task order saved');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to reorder tasks');
      }
    },
    [selectedEpicId, isAdmin, displayBoard, issueById, invalidateBoard],
  );

  const statusMutation = useMutation({
    mutationFn: ({ issueId, status }) => {
      // API requires `resolution` for closed statuses other than Closed (e.g. Fixed for bugs).
      const resolution = status === 'Fixed' ? 'Fixed' : undefined;
      return updateIssueStatus(issueId, status, resolution);
    },
    onSuccess: (_, { issueId, status }) => {
      const parentStoryId = (boardData?.allIssues || []).find(
        (i) => String(i.issueid) === String(issueId),
      )?.parent_id;
      if (parentStoryId) {
        const patchEntry = (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((t) =>
            String(t.issueid) === String(issueId) ? { ...t, status } : t,
          );
        };
        qc.setQueryData(['story-subtasks', parentStoryId], patchEntry);
        qc.setQueryData(['selected-story-subtasks', parentStoryId], patchEntry);
        qc.invalidateQueries({ queryKey: ['story-subtasks', parentStoryId] });
        qc.invalidateQueries({ queryKey: ['selected-story-subtasks', parentStoryId] });
      }
      invalidateBoard();
      toast.success('Status updated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update status'),
  });

  const deleteEpicMutation = useMutation({
    mutationFn: (issueId) => deleteIssue(issueId),
    onSuccess: (_, issueId) => {
      invalidateBoard();
      setSelectedEpicId((cur) => (String(cur) === String(issueId) ? null : cur));
      setEpicDetailModalId((cur) => (String(cur) === String(issueId) ? null : cur));
      toast.success('Epic deleted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete epic'),
  });

  const deleteStoryMutation = useMutation({
    mutationFn: (issueId) => deleteIssue(issueId),
    onSuccess: () => { invalidateBoard(); toast.success('Story deleted'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete story'),
  });

  const reassignMutation = useMutation({
    mutationFn: ({ issueId, assigneeId }) => reassignIssue(issueId, assigneeId),
    onSuccess: () => { invalidateBoard(); toast.success('Assignee updated'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to reassign'),
  });

  const sprintStatusMutation = useMutation({
    mutationFn: ({ id, status, force }) => updateSprintStatus(id, status, force),
    onSuccess: () => {
      invalidateSprints();
      invalidateBoard();
      toast.success('Sprint updated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update sprint'),
  });

  const deleteSprintMutation = useMutation({
    mutationFn: ({ id, deleteIssues }) => deleteSprint(id, deleteIssues),
    onSuccess: (response) => {
      invalidateSprints();
      invalidateBoard();
      qc.invalidateQueries({ queryKey: ['backlog', selectedProject] });
      setSelectedSprint(null);
      const message = response?.data?.details || 'Sprint deleted successfully';
      toast.success(message);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Cannot delete sprint'),
  });

  // Reset the dialog form whenever it closes — otherwise switching sprints
  // and reopening the dialog leaks the previous sprint's draft text.
  const closeReleaseDialog = () => {
    setShowRelease(false);
    setReleaseData({ version: '', release_notes: '' });
  };

  const releaseMutation = useMutation({
    mutationFn: (data) => releaseSprint(selectedSprint, data),
    onSuccess: () => {
      invalidateSprints();
      invalidateBoard();
      qc.invalidateQueries({ queryKey: ['releases'] });
      closeReleaseDialog();
      toast.success('Sprint released!');
    },
    onError: () => toast.error('Failed to release sprint'),
  });

  const moveMutation = useMutation({
    mutationFn: ({ issueId, sprintId }) => moveIssueToSprint(issueId, sprintId),
    onSuccess: (response, { sprintId }) => {
      qc.invalidateQueries({ queryKey: ['backlog', selectedProject] });
      qc.invalidateQueries({ queryKey: ['sprints', selectedProject] });
      invalidateBoard();
      const movedCount = response?.data?.movedCount;
      if (movedCount > 1) {
        toast.success(
          sprintId
            ? 'Work item and nested stories/tasks moved to sprint'
            : 'Work item and nested stories/tasks moved to backlog',
        );
      } else {
        toast.success(sprintId ? 'Added to sprint' : 'Moved to backlog');
      }
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || 'Failed to move issue'),
  });

  const handleRefresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['sprints', selectedProject] }),
      qc.invalidateQueries({ queryKey: ['board'] }),
      qc.invalidateQueries({ queryKey: ['backlog', selectedProject] }),
    ]);
    toast.success('Board data refreshed');
  };

  // Derive available FYs from loaded sprints (for the optional filter dropdown)
  const availableFYs = [...new Set(
    sprintsInScope.map(s => getFY(s.start_date)).filter(Boolean)
  )].sort((a, b) => b - a);

  // Filter sprints by FY only when one is selected
  const fyFilteredSprints = selectedFY
    ? (() => { const { start, end } = fyRange(selectedFY); return sprintsInScope.filter(s => { const sd = new Date(s.start_date); return sd >= start && sd <= end; }); })()
    : sprintsInScope;
  // Stewards see all sprint statuses; others see Active only, or Planning+Active with sprint.planning.view
  const filteredSprints = filterSprintsForBoardCarousel(fyFilteredSprints, role, user?.permissions);

  const selectedSprintKey = selectedSprint === null || selectedSprint === undefined
    ? null
    : String(selectedSprint);
  const activeSprint = sprintsInScope.find(s => String(s.id) === selectedSprintKey);
  const activeSprintGoal = parseSprintGoal(activeSprint?.goal || '');
  const effectiveProjectId = selectedProject || activeSprint?.project_id || activeSprint?.projectid || null;
  const selectedProjectRow = useMemo(
    () => orderedProjects.find((p) => String(p.projectid) === String(effectiveProjectId || selectedProject)),
    [orderedProjects, effectiveProjectId, selectedProject],
  );
  const workflowDepartment = normalizeDepartment(
    selectedProjectRow?.department || effectiveBoardDepartment,
  );
  const usesSprints = departmentUsesSprints(workflowDepartment);
  const boardPageLabel = getWorkBoardLabel(workflowDepartment);
  const cycleLabel = getCycleLabel(workflowDepartment);
  const progress = activeSprint && activeSprint.total_issues > 0
    ? Math.round((activeSprint.done_issues / activeSprint.total_issues) * 100) : 0;

  const { data: projectMembersRawBoard } = useQuery({
    queryKey: ['project-members', effectiveProjectId],
    queryFn: () => api.get(`/projects/${effectiveProjectId}/members`).then((r) => r.data),
    enabled: !!effectiveProjectId,
  });
  const boardProjectMembers = useMemo(
    () => normalizeProjectMembersResponse(projectMembersRawBoard),
    [projectMembersRawBoard],
  );
  const boardBaseAssignableUsers = useMemo(() => {
    const nonAdmin = (list) =>
      (list || []).filter((u) => !['admin', 'superadmin'].includes(String(u.role || '').toLowerCase()));
    if (boardProjectMembers.length > 0) return nonAdmin(boardProjectMembers);
    return nonAdmin(userListForAssign);
  }, [boardProjectMembers, userListForAssign]);

  const hasEpics = boardData?.epicsBoard && EPIC_FLOW_COLUMNS.some((c) => (boardData.epicsBoard[c] || []).length > 0);

  const projectCarouselStamp = useMemo(
    () => `${orderedProjects.map((p) => p.projectid).join(',')}|${isAdmin ? 1 : 0}`,
    [orderedProjects, isAdmin]
  );
  const sprintCarouselStamp = useMemo(
    () => filteredSprints.map((s) => s.id).join(','),
    [filteredSprints]
  );
  const projectCarouselNav = useCarouselScrollNav(projectCarouselStamp);
  const sprintCarouselNav = useCarouselScrollNav(sprintCarouselStamp);

  const projectCarouselItemCount = (isAdmin ? 1 : 0) + orderedProjects.length;
  const showProjectCarouselArrows = projectCarouselItemCount >= CAROUSEL_ARROW_MIN_COUNT;
  const showSprintCarouselArrows = filteredSprints.length >= CAROUSEL_ARROW_MIN_COUNT;

  const epicMoveSprints = useMemo(() => {
    if (!effectiveProjectId || !sprintsInScope.length) return [];
    return sprintsInScope.filter(
      (s) =>
        String(s.project_id) === String(effectiveProjectId) &&
        SPRINT_WORK_STATUSES.includes(s.status)
    );
  }, [sprintsInScope, effectiveProjectId]);

  /**
   * Same eligibility as moving an epic — a sprint in this project that still
   * accepts work (Backlog drafts included for stewards, who are the only ones
   * who can see them). Shared so the two pickers cannot drift apart.
   */
  const moveTargetSprints = epicMoveSprints;

  /** Toggle epic filter on the board (click card). Same epic again clears selection. */
  const selectEpic = useCallback((issueId) => {
    const id = String(issueId);
    setSelectedEpicId((prev) => (prev != null && String(prev) === id ? null : id));
  }, []);

  /** Open epic edit / AI / move dialog (Open button). */
  const openEpicModal = useCallback((issueId) => {
    setSelectedEpicId(String(issueId));
    setEpicDetailModalId(String(issueId));
  }, []);

  const sprintLabelForEpic = useCallback(
    (epicRow) => {
      const sid = epicRow.sprint_id ?? selectedSprint;
      if (sid == null || sid === '') {
        return isBacklogBoardView ? 'Backlog' : '';
      }
      const s = sprints.find((x) => String(x.id) === String(sid));
      if (s?.sprint_name) return `${s.sprint_name} (${s.status})`;
      return activeSprint?.sprint_name ? `${activeSprint.sprint_name} (${activeSprint.status})` : '';
    },
    [sprints, selectedSprint, activeSprint, isBacklogBoardView],
  );

  const epicModalIssue = useMemo(() => {
    if (!epicDetailModalId || !boardData?.allIssues) return null;
    return boardData.allIssues.find(
      (i) => String(i.issueid) === String(epicDetailModalId) && i.issuetype === 'Epic',
    ) || null;
  }, [epicDetailModalId, boardData]);

  const epicModalSprintDisplay = useMemo(() => {
    if (!epicModalIssue) return '—';
    const sid = epicModalIssue.sprint_id ?? selectedSprint;
    if (sid == null || sid === '') {
      return isBacklogBoardView ? 'Backlog' : '—';
    }
    const s = sprints.find((x) => String(x.id) === String(sid));
    return s?.sprint_name ? `${s.sprint_name} (${s.status})` : '—';
  }, [epicModalIssue, sprints, selectedSprint, isBacklogBoardView]);

  /** Board refetch updates allIssues but taskModal.story does not; re-resolve so save clears dirty state. */
  const storyForKanbanModal = useMemo(() => {
    if (!taskModal?.story) return null;
    const sid = String(taskModal.story.issueid);
    const fresh = boardData?.allIssues?.find((i) => String(i.issueid) === sid);
    return fresh || taskModal.story;
  }, [taskModal, boardData?.allIssues]);

  return (
    <div style={{ padding: '1.5rem 2rem', background: C.sunken, minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: C.ink }}>{boardPageLabel}</h1>
          <p style={{ margin: '0.15rem 0 0', color: C.inkSoft, fontSize: '0.85rem' }}>
            {usesSprints ? 'Plan, track and release sprints' : 'Plan and track work across your projects'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={handleRefresh} style={btnStyle(C.done, true)}>
            ↻ Refresh
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                if (!selectedProject) {
                  toast.error('Select a project first');
                  return;
                }
                setShowBacklog(true);
              }}
              style={btnStyle(C.inkSoft, showBacklog)}
            >
              Backlog
              {backlogPayload?.meta?.displayCount != null
                ? ` (${backlogPayload.meta.displayCount})`
                : ''}
            </button>
          )}
          {isAdmin && usesSprints && (
            <button onClick={() => setShowSprintForm(true)} style={btnStyle(C.accent)}>+ New {cycleLabel}</button>
          )}
          {usesSprints && effectiveProjectId && isSelectedProjectInScope && (
            <button
              type="button"
              onClick={() => setShowNotebookModal(true)}
              style={btnStyle(C.done)}
            >
              📓 Daily Notebook
            </button>
          )}
          <button onClick={() => setShowIssueForm(true)} style={btnStyle(C.accentInk)}>+ New Task</button>
        </div>
      </div>

      {/* ── Project Selector (Prominent Cards) ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: C.ink }}>
              Select Project
            </h3>
            <span style={{ fontSize: '0.72rem', color: C.inkFaint, fontWeight: 500 }}>
              {showProjectCarouselArrows
                ? 'Active projects first, then last session order; order stays fixed until you reload. Use the side arrows or drag to scroll.'
                : 'Active projects first, then last session order; it updates when you reload the page.'}
            </span>
          </div>
        </div>
        
        {/* No projects in current department view */}
        {projects.length === 0 && isAdmin && isProjectsFetched && (
          <div style={{
            padding: '20px 24px',
            background: C.sunken,
            border: `1.5px dashed ${C.upcoming}`,
            borderRadius: 12,
            color: C.inkSoft,
            fontSize: '0.88rem',
            fontWeight: 600,
            marginBottom: 12,
          }}>
            No projects in {departmentView || 'all departments'}. Switch the department view above or create a project for this department.
          </div>
        )}

        {/* No projects assigned — non-admin users */}
        {projects.length === 0 && !isAdmin && (
          <div style={{
            padding: '24px 28px',
            background: C.warnSoft,
            border: `1.5px dashed ${C.warn}`,
            borderRadius: 12,
            color: C.warn,
            fontSize: '0.9rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: '1.3rem' }}>📋</span>
            You are not assigned to any project yet. Please inform your Manager to add you to a project.
          </div>
        )}

        <div style={{ width: '100%', maxWidth: '100%' }}>
          <div style={pickerCarouselShell}>
            <div
              ref={projectCarouselNav.ref}
              style={{
                ...pickerCarouselScrollTrack,
                paddingLeft: showProjectCarouselArrows ? CAROUSEL_EDGE_BTN_W : 0,
                paddingRight: showProjectCarouselArrows ? CAROUSEL_EDGE_BTN_W : 0,
              }}
            >
            <div style={pickerCarouselInner}>
          {/* All Projects Card — visible only to admin/manager roles */}
          {isAdmin && <div
            onClick={() => handleProjectSelect('')}
            style={{
              flex: '0 0 auto',
              minWidth: 180,
              padding: '14px 18px',
              background: selectedProject === '' ? C.accentInk : '#fff',
              color: selectedProject === '' ? '#fff' : C.ink,
              border: selectedProject === '' ? `2px solid ${C.accentInk}` : `2px solid ${C.line}`,
              borderRadius: 12,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: selectedProject === '' ? '0 4px 12px rgba(139, 92, 246, 0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={e => {
              if (selectedProject !== '') {
                e.currentTarget.style.borderColor = C.accent;
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(167, 139, 250, 0.2)';
              }
            }}
            onMouseLeave={e => {
              if (selectedProject !== '') {
                e.currentTarget.style.borderColor = C.line;
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
              }
            }}
          >
            <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: 4 }}>
              🌐 All Projects
            </div>
            {/* Name, then the counts on one line — the oversized figure made
                this strip twice as tall as it needs to be. */}
            <div style={{ fontSize: '0.78rem', fontWeight: 700, opacity: 0.85 }}>
              {allSprints.filter(s => s.status === 'Active').length} Active · {allSprints.filter(s => s.status === 'Planning').length} Planning
            </div>
          </div>}

          {/* Individual Project Cards */}
          {orderedProjects.map(project => {
            const projectSprints = allSprints.filter(s => String(s.project_id).trim() === String(project.projectid).trim());
            const activeSprint = projectSprints.find(s => s.status === 'Active');
            const isActive = String(selectedProject) === String(project.projectid);
            
            return (
              <div
                key={project.projectid}
                onClick={() => handleProjectSelect(project.projectid)}
                style={{
                  flex: '0 0 auto',
                  minWidth: 200,
                  padding: '14px 18px',
                  background: isActive ? C.accentInk : C.surface,
                  color: isActive ? '#fff' : C.ink,
                  border: isActive ? `2px solid ${C.accentInk}` : `2px solid ${C.line}`,
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: isActive ? `0 4px 12px ${C.accentInk}20` : '0 1px 3px rgba(0,0,0,0.05)',
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = C.accent;
                    e.currentTarget.style.boxShadow = `0 2px 8px ${C.accent}30`;
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = C.line;
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }
                }}
              >
                <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {project.projectname}
                  </span>
                </div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, opacity: 0.85 }}>
                  {projectSprints.filter(s => s.status === 'Active').length} Active · {projectSprints.filter(s => s.status === 'Planning').length} Planning
                </div>
                {activeSprint && (
                  <div style={{
                    fontSize: '0.65rem',
                    opacity: 0.9,
                    marginTop: 6,
                    padding: '2px 6px',
                    background: isActive ? 'rgba(255,255,255,0.2)' : C.accentSoft,
                    borderRadius: 4,
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    🏃 {activeSprint.sprint_name}
                  </div>
                )}
              </div>
            );
          })}
            </div>
            </div>
            {showProjectCarouselArrows && (
              <>
                <button
                  type="button"
                  aria-label="Scroll projects left"
                  disabled={!projectCarouselNav.edge.canLeft}
                  onClick={() => projectCarouselNav.scrollStep(-1)}
                  style={{
                    ...carouselEdgeButtonBase,
                    left: 0,
                    borderTopLeftRadius: 12,
                    borderBottomLeftRadius: 12,
                    opacity: projectCarouselNav.edge.canLeft ? 1 : 0.42,
                    cursor: projectCarouselNav.edge.canLeft ? 'pointer' : 'not-allowed',
                    pointerEvents: projectCarouselNav.edge.canLeft ? 'auto' : 'none',
                  }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label="Scroll projects right"
                  disabled={!projectCarouselNav.edge.canRight}
                  onClick={() => projectCarouselNav.scrollStep(1)}
                  style={{
                    ...carouselEdgeButtonBase,
                    right: 0,
                    borderTopRightRadius: 12,
                    borderBottomRightRadius: 12,
                    opacity: projectCarouselNav.edge.canRight ? 1 : 0.42,
                    cursor: projectCarouselNav.edge.canRight ? 'pointer' : 'not-allowed',
                    pointerEvents: projectCarouselNav.edge.canRight ? 'auto' : 'none',
                  }}
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Sprint Selector (Prominent Cards) ── */}
      {usesSprints && (isSelectedProjectInScope && (selectedProject || sprintsInScope.length > 0)) && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: C.ink }}>
              🏃 Select {cycleLabel}
            </h3>
            <span style={{ fontSize: '0.72rem', color: C.inkFaint, fontWeight: 500 }}>
              {showSprintCarouselArrows
                ? 'Blue arrows scroll the sprint row (or drag / wheel). '
                : ''}
              Choose a {cycleLabel.toLowerCase()} to focus the Kanban on that {cycleLabel.toLowerCase()}, or leave none selected to use the project backlog.
              Click the selected {cycleLabel.toLowerCase()} again to switch back to backlog.
            </span>
          </div>
          
          <div style={{ width: '100%', maxWidth: '100%' }}>
            <div style={pickerCarouselShell}>
            <div
              ref={sprintCarouselNav.ref}
              style={{
                ...pickerCarouselScrollTrack,
                paddingLeft: showSprintCarouselArrows ? CAROUSEL_EDGE_BTN_W : 0,
                paddingRight: showSprintCarouselArrows ? CAROUSEL_EDGE_BTN_W : 0,
              }}
            >
            <div style={pickerCarouselInner}>
            {filteredSprints.map((sprint) => {
              const isActive = String(selectedSprint) === String(sprint.id);
              const statusColor = STATUS_BADGE[sprint.status] || STATUS_BADGE.Planning;
              const st = (v) => Number(v) || 0;
              const openC = st(sprint.tasks_open);
              const progC = st(sprint.tasks_in_progress);
              const testC = st(sprint.tasks_testing);
              const doneC = st(sprint.done_issues);
              const blockedC = st(sprint.tasks_blocked);
              const totalT = st(sprint.total_issues);

              return (
                <div
                  key={sprint.id}
                  onClick={() => {
                    if (String(selectedSprint) === String(sprint.id)) {
                      setSelectedSprint(null);
                    } else {
                      setSelectedSprint(String(sprint.id));
                    }
                  }}
                  style={{
                    flex: '0 0 auto',
                    minWidth: 248,
                    padding: '14px 18px',
                    background: isActive ? C.accentInk : '#fff',
                    color: isActive ? '#fff' : C.ink,
                    border: isActive ? `2px solid ${C.accentInk}` : `2px solid ${C.line}`,
                    borderRadius: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: isActive ? '0 4px 12px rgba(18, 114, 179, 0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
                    position: 'relative',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = C.accent;
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(44, 143, 199, 0.2)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = C.line;
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                    }
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    padding: '3px 8px',
                    background: isActive ? 'rgba(255,255,255,0.2)' : statusColor.bg,
                    color: isActive ? '#fff' : statusColor.color,
                    borderRadius: 12,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                  }}>
                    {sprint.status}
                  </div>
                  
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: 4, paddingRight: 70 }}>
                    {sprint.sprint_name}
                  </div>
                  {sprint.projectname && (
                    <div style={{ fontSize: '0.7rem', opacity: 0.8, marginBottom: 4 }}>
                      Project: {sprint.projectname}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      opacity: isActive ? 0.88 : 0.65,
                      marginTop: 6,
                      color: isActive ? '#fff' : C.inkSoft,
                    }}
                  >
                    Tasks by status
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      columnGap: 10,
                      rowGap: 2,
                      marginTop: 4,
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      lineHeight: 1.35,
                      color: isActive ? '#fff' : C.ink,
                    }}
                  >
                    <span>Open: {openC}</span>
                    <span style={{ textAlign: 'right' }}>In progress: {progC}</span>
                    <span>{issueStatusLabel('Testing', boardDepartment)}: {testC}</span>
                    <span style={{ textAlign: 'right' }}>{issueStatusLabel('Done', boardDepartment)}: {doneC}</span>
                  </div>
                  {blockedC > 0 ? (
                    <div
                      style={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        marginTop: 6,
                        color: isActive ? 'rgba(254, 226, 226, 0.95)' : C.warn,
                      }}
                    >
                      Blocked: {blockedC}
                    </div>
                  ) : null}
                  <div
                    style={{
                      fontSize: '0.62rem',
                      opacity: isActive ? 0.75 : 0.55,
                      marginTop: 6,
                      fontWeight: 600,
                      color: isActive ? '#fff' : C.inkSoft,
                    }}
                  >
                    {totalT} task{totalT === 1 ? '' : 's'} in sprint
                  </div>
                </div>
              );
            })}
            </div>
            </div>
            {showSprintCarouselArrows && (
              <>
                <button
                  type="button"
                  aria-label="Scroll sprints left"
                  disabled={!sprintCarouselNav.edge.canLeft}
                  onClick={() => sprintCarouselNav.scrollStep(-1)}
                  style={{
                    ...carouselEdgeButtonBase,
                    left: 0,
                    borderTopLeftRadius: 12,
                    borderBottomLeftRadius: 12,
                    opacity: sprintCarouselNav.edge.canLeft ? 1 : 0.42,
                    cursor: sprintCarouselNav.edge.canLeft ? 'pointer' : 'not-allowed',
                    pointerEvents: sprintCarouselNav.edge.canLeft ? 'auto' : 'none',
                  }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label="Scroll sprints right"
                  disabled={!sprintCarouselNav.edge.canRight}
                  onClick={() => sprintCarouselNav.scrollStep(1)}
                  style={{
                    ...carouselEdgeButtonBase,
                    right: 0,
                    borderTopRightRadius: 12,
                    borderBottomRightRadius: 12,
                    opacity: sprintCarouselNav.edge.canRight ? 1 : 0.42,
                    cursor: sprintCarouselNav.edge.canRight ? 'pointer' : 'not-allowed',
                    pointerEvents: sprintCarouselNav.edge.canRight ? 'auto' : 'none',
                  }}
                >
                  ›
                </button>
              </>
            )}
            </div>
          </div>
        </div>
      )}

      {/* ── Sprint Actions (Only show when sprint is selected) ── */}
      {usesSprints && activeSprint && (
        <div style={{ background: 'white', borderRadius: 12, border: `1px solid ${C.line}`, padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>

            {/* Edit — Planning / Active / Review get full edits; Released / Closed
                are rename-only (the modal enforces this and the backend rejects
                anything else for those statuses). */}
            {isAdmin && activeSprint && (
              <button
                onClick={() => setEditingSprint(activeSprint)}
                title={
                  ['Released', 'Closed'].includes(activeSprint.status)
                    ? `Rename ${activeSprint.status.toLowerCase()} sprint`
                    : 'Edit sprint details'
                }
                style={{ padding: '6px 10px', borderRadius: 7, border: `1px solid ${C.accentSoft}`, background: C.accentSoft, color: C.accentInk, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}
              >
                {['Released', 'Closed'].includes(activeSprint.status) ? '✏️ Rename' : '✏️ Edit'}
              </button>
            )}


            {/* Divider before lifecycle actions */}
            {isAdmin && selectedSprint && (
              <span style={{ width: 1, height: 24, background: C.line, display: 'inline-block', margin: '0 2px' }} />
            )}

            {/* Lifecycle: Share / Start / Complete / Release — admin/manager only with confirmations */}
            {isAdmin && selectedSprint && activeSprint?.status === 'Backlog' && (
              <button onClick={() => {
                setConfirmDialog({
                  title: 'Move to Planning',
                  message: `Share "${activeSprint.sprint_name}" with the team?`,
                  details: 'Everyone on the project will see this sprint and can add and track work in it. It stays out of the Issues "Active sprint" view until you start it.',
                  confirmLabel: 'Move to Planning',
                  confirmColor: C.accentInk,
                  onConfirm: () => sprintStatusMutation.mutate({ id: selectedSprint, status: 'Planning' }),
                });
              }}
                style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.accentSoft}`, background: C.accentSoft, color: C.accentInk, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}>
                Move to Planning
              </button>
            )}
            {isAdmin && selectedSprint && activeSprint?.status === 'Planning' && (
              <button onClick={() => {
                setConfirmDialog({
                  title: 'Back to Backlog',
                  message: `Hide "${activeSprint.sprint_name}" from the team?`,
                  details: 'It becomes a private draft again. Work already in it stays put — only who can see the sprint changes.',
                  confirmLabel: 'Back to Backlog',
                  confirmColor: C.inkSoft,
                  onConfirm: () => sprintStatusMutation.mutate({ id: selectedSprint, status: 'Backlog' }),
                });
              }}
                style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.line}`, background: '#fff', color: C.inkSoft, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}>
                Back to Backlog
              </button>
            )}
            {isAdmin && selectedSprint && activeSprint?.status === 'Planning' && (
              <button onClick={() => {
                setConfirmDialog({
                  title: '▶ Start Sprint',
                  message: `Start "${activeSprint.sprint_name}"?`,
                  details: 'This will make it Active. Any other active sprint in this project will move to Review.',
                  confirmLabel: 'Start Sprint',
                  confirmColor: C.accentInk,
                  onConfirm: () => sprintStatusMutation.mutate({ id: selectedSprint, status: 'Active' }),
                });
              }}
                style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.accentSoft}`, background: C.accentSoft, color: C.accentInk, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}>
                ▶ Start Sprint
              </button>
            )}
            {isAdmin && selectedSprint && activeSprint?.status === 'Active' && (
              <button onClick={() => {
                const boardOpenCount = boardData?.board
                  ? (boardData.board['Open']?.length || 0)
                    + (boardData.board['In Progress']?.length || 0)
                    + (boardData.board['Testing']?.length || 0)
                    + (boardData.board['Blocked']?.length || 0)
                  : 0;
                const epicOpenCount = boardData?.epicsBoard
                  ? (boardData.epicsBoard.Planning?.length || 0) + (boardData.epicsBoard.Active?.length || 0)
                  : 0;
                const openCount = boardOpenCount + epicOpenCount;
                const doneCount = boardData?.board?.['Done']?.length || 0;
                const epicDoneCount = boardData?.epicsBoard?.Complete?.length || 0;
                
                if (openCount > 0) {
                  setConfirmDialog({
                    title: '⚠️ Open Issues Found',
                    message: `Sprint has ${openCount} open issue(s)`,
                    warning: true,
                    details: `• Done: ${doneCount} story/task${doneCount === 1 ? '' : 's'}${epicDoneCount ? `, ${epicDoneCount} epic${epicDoneCount === 1 ? '' : 's'}` : ''}\n• Still open: ${openCount}${epicOpenCount > 0 ? ` (includes ${epicOpenCount} epic${epicOpenCount === 1 ? '' : 's'} not marked Complete)` : ''}\n\nMove open items to Done or another sprint before completing.`,
                    confirmLabel: 'Complete Anyway',
                    confirmColor: C.warn,
                    onConfirm: () => sprintStatusMutation.mutate({ id: selectedSprint, status: 'Review', force: true }),
                  });
                } else {
                  setConfirmDialog({
                    title: 'Complete Sprint',
                    message: `Complete "${activeSprint.sprint_name}"?`,
                    details: `All sprint board work is done (${doneCount} story/task${doneCount === 1 ? '' : 's'}${epicDoneCount ? `, ${epicDoneCount} epic${epicDoneCount === 1 ? '' : 's'}` : ''}). Sprint will move to Review status.`,
                    confirmLabel: 'Complete Sprint',
                    confirmColor: C.done,
                    onConfirm: () => sprintStatusMutation.mutate({ id: selectedSprint, status: 'Review' }),
                  });
                }
              }}
                style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.warnSoft}`, background: C.warnSoft, color: C.warn, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}>
                Complete Sprint
              </button>
            )}
            {isAdmin && selectedSprint && activeSprint?.status === 'Review' && (
              <button onClick={() => {
                setConfirmDialog({
                  title: 'Release Sprint',
                  message: `Release "${activeSprint.sprint_name}"?`,
                  details: 'This will mark it as Released and create a release record.',
                  confirmLabel: 'Continue to Release',
                  confirmColor: C.done,
                  onConfirm: () => {
                    setReleaseData({ version: '', release_notes: '' });
                    setShowRelease(true);
                  },
                });
              }}
                style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.doneSoft}`, background: C.doneSoft, color: C.done, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}>
                Release Sprint
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Sprint Info Bar ── */}
      {usesSprints && activeSprint && (
        <div style={{
          background: '#fff', borderRadius: 12, padding: '0.9rem 1.25rem',
          border: `1px solid ${C.line}`, marginBottom: '1.25rem',
          display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: C.inkFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sprint</div>
            <div style={{ fontWeight: 800, color: C.ink, fontSize: '0.95rem' }}>{activeSprint.sprint_name}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', color: C.inkFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</div>
            <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700, background: STATUS_BADGE[activeSprint.status]?.bg, color: STATUS_BADGE[activeSprint.status]?.color }}>
              {activeSprint.status}
            </span>
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', color: C.inkFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Dates</div>
            <div style={{ fontSize: '0.83rem', color: C.ink }}>
              {new Date(activeSprint.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} →{' '}
              {new Date(activeSprint.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', color: C.inkFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Progress</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 110, height: 7, background: C.line, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: C.done, borderRadius: 4, width: `${progress}%`, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: C.done }}>{progress}% ({activeSprint.done_issues}/{activeSprint.total_issues})</span>
            </div>
          </div>
          {(activeSprintGoal.goal || activeSprintGoal.brdLink || activeSprintGoal.figmaLink) && (
            <div style={{ flex: 1, minWidth: 220 }}>
              {activeSprintGoal.goal && (
                <>
                  <div style={{ fontSize: '0.7rem', color: C.inkFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Goal</div>
                  <div style={{ fontSize: '0.83rem', color: C.inkSoft, fontStyle: 'italic' }}>"{activeSprintGoal.goal}"</div>
                </>
              )}
              {(activeSprintGoal.brdLink || activeSprintGoal.figmaLink) && (
                <>
                  <div style={{
                    fontSize: '0.7rem',
                    color: C.inkFaint,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginTop: activeSprintGoal.goal ? 8 : 0,
                  }}
                  >
                    Reference links
                  </div>
                  <SprintReferenceLinks
                    brdLink={activeSprintGoal.brdLink}
                    figmaLink={activeSprintGoal.figmaLink}
                    style={{ marginTop: 6 }}
                  />
                </>
              )}
            </div>
          )}
          {Number(activeSprint.blockers) > 0 && (
            <div style={{ background: C.dangerSoft, border: `1px solid ${C.dangerSoft}`, borderRadius: 8, padding: '4px 10px', color: C.danger, fontWeight: 700, fontSize: '0.82rem' }}>
              {activeSprint.blockers} Blocker{activeSprint.blockers > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      <BacklogDialog
        open={showBacklog}
        onClose={() => setShowBacklog(false)}
        projectName={projects.find((p) => String(p.projectid) === String(selectedProject))?.projectname}
        backlogPayload={backlogPayload}
        isLoading={backlogLoading}
        sprintList={sprints}
        onMoveToSprint={(issueId, sprintId) =>
          moveMutation.mutate({ issueId, sprintId: Number(sprintId) })
        }
        onOpenEpic={(epic) => {
          setShowBacklog(false);
          selectEpic(epic.issueid);
          openEpicModal(epic.issueid);
        }}
        onOpenStory={(story) => {
          setShowBacklog(false);
          setSelectedDirectTask(story);
          setTaskModal(null);
        }}
        onAddStory={() => {
          setShowBacklog(false);
          setShowBacklogForm(true);
        }}
      />

      <>
      {/* ── Kanban Board ── */}
      {boardLoading ? (
        // Column-shaped so the board keeps its footprint while it loads —
        // a centred spinner collapses the page and then shoves it back open.
        <div
          role="status"
          aria-busy="true"
          aria-label="Loading board"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
            gap: 12,
          }}
        >
          {Array.from({ length: isMobile ? 1 : 4 }, (_, col) => (
            <div key={col} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Skeleton height={30} radius={8} />
              <Skeleton height={92} radius={10} />
              <Skeleton height={92} radius={10} />
              <Skeleton height={92} radius={10} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Sprint-scoped burndown. Only for a real sprint — the backlog has
              no start/end dates to burn down against. Hidden on mobile, where
              a chart this wide can't be read. */}
          {!isMobile && !isBacklogBoardView && activeSprint && (
            <SprintBurndown sprint={activeSprint} issues={boardData?.allIssues} />
          )}
          {boardData && (
            <EpicBoardStrip
              epicsBoard={boardData.epicsBoard}
              onSelectEpic={selectEpic}
              onOpenEpicModal={openEpicModal}
              selectedEpicId={selectedEpicId}
              sprintLabelForEpic={sprintLabelForEpic}
            />
          )}
          {selectedEpicId && selectedEpic && (() => {
            const { title: epicTitle, details: epicDetails } = epicTitleAndDetails(selectedEpic.description);
            const hasRefLinks = Boolean(activeSprintGoal.brdLink || activeSprintGoal.figmaLink);
            return (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
              marginBottom: '0.85rem', padding: '0.75rem 1rem',
              background: C.accentSoft, border: `1px solid ${C.accentSoft}`, borderRadius: 10,
            }}>
              <button
                type="button"
                onClick={() => setSelectedEpicId(null)}
                style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.accent}`, background: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', color: C.accentInk, flexShrink: 0 }}
              >
                ← All epics
              </button>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: C.ink, lineHeight: 1.35 }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', color: C.inkSoft, fontWeight: 700 }}>#{selectedEpic.issueid}</span>
                  {' · '}
                  {epicTitle}
                  <span style={{ fontWeight: 600, color: C.inkSoft }}> · Stories & tasks</span>
                </div>
                {epicDetails ? (
                  <div style={{ fontSize: '0.78rem', color: C.inkSoft, marginTop: 6, lineHeight: 1.45 }}>
                    {epicDetails}
                  </div>
                ) : null}
                {hasRefLinks ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: epicDetails ? 8 : 6 }}>
                    <span style={{
                      fontSize: '0.68rem',
                      color: C.inkSoft,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                    >
                      Reference links
                    </span>
                    <SprintReferenceLinks
                      brdLink={activeSprintGoal.brdLink}
                      figmaLink={activeSprintGoal.figmaLink}
                    />
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => openEpicModal(selectedEpic.issueid)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: `1px solid ${C.accentInk}`,
                  background: C.accentInk,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  flexShrink: 0,
                  alignSelf: 'center',
                }}
              >
                Open epic details
              </button>
            </div>
            );
          })()}
          {selectedEpicId && epicBoardHasNoCards && (
            <div style={{
              marginBottom: '0.75rem', padding: '0.65rem 0.85rem',
              background: C.warnSoft, border: `1px solid ${C.warnSoft}`, borderRadius: 10,
              fontSize: '0.82rem', color: C.warn, lineHeight: 1.45,
            }}>
              No stories or tasks on the board for this epic yet. After approving AI drafts, use <strong>Refresh</strong> on the sprint board (or revisit this page) so new issues load.
              If items still do not appear, confirm each approved story has this epic as its parent in the database.
            </div>
          )}
          {!selectedEpicId && hasEpics && (
            <div style={{ fontSize: '0.8rem', color: C.inkSoft, marginBottom: '0.65rem', fontWeight: 600 }}>
              {isBacklogBoardView ? 'Backlog' : 'Sprint'} board below shows work <span style={{ color: C.ink }}>not</span>{' '}
              linked to an Epic. <strong>Click an epic card</strong> to filter stories & tasks below; use{' '}
              <strong>Open</strong> on a card for the epic details dialog.
            </div>
          )}
          {/* Five 1fr columns on a phone come out ~54px wide, and because the
              columns carry no minWidth they silently squash rather than giving
              you a scrollbar. On mobile the board becomes a snap carousel: one
              column at a time, swipe for the next. */}
          <div
            className={isMobile ? 'snap-x hide-scrollbar' : undefined}
            style={
              isMobile
                ? {
                    display: 'flex',
                    overflowX: 'auto',
                    gap: '0.85rem',
                    alignItems: 'flex-start',
                    // Bleed to the screen edges so a swipe feels like the
                    // whole board is moving, not a box inside the page.
                    margin: '0 -1rem',
                    padding: '0 1rem 0.5rem',
                  }
                : { display: 'grid', gridTemplateColumns: `repeat(${visibleBoardColumns.length}, 1fr)`, gap: '0.85rem', alignItems: 'start' }
            }
          >
            {visibleBoardColumns.map(col => {
              const issues = displayBoard[col] || [];
              return (
                <div
                  key={col}
                  className={isMobile ? 'snap-start' : undefined}
                  style={{
                    background: '#fff',
                    borderRadius: 12,
                    border: `1px solid ${C.line}`,
                    overflow: 'hidden',
                    ...(isMobile ? { flex: '0 0 85%', minWidth: 0 } : null),
                  }}
                >
                  <div style={{ padding: '0.7rem 0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `3px solid ${COL_COLORS[col]}`, background: C.sunken }}>
                    <span style={{ fontWeight: 800, color: COL_COLORS[col], fontSize: '0.85rem' }}>{issueStatusLabel(col, boardDepartment)}</span>
                    <span style={{ background: `${COL_COLORS[col]}22`, color: COL_COLORS[col], borderRadius: 20, padding: '1px 8px', fontSize: '0.78rem', fontWeight: 700 }}>
                      {issues.length}
                    </span>
                  </div>
                  <div style={{ padding: '0.65rem', minHeight: 80 }}>
                    {issues.length === 0
                      ? <div style={{ textAlign: 'center', color: C.upcoming, fontSize: '0.78rem', padding: '0.75rem 0' }}>
                          {!selectedProject ? 'Select a project' : 'Empty'}
                        </div>
                      : issues.map((issue) => {
                          const reorderInOpenColumn = col === 'Open';
                          const epicStoriesInCol =
                            isAdmin && reorderInOpenColumn
                              ? issues.filter(
                                  (i) =>
                                    i.issuetype === 'Story' &&
                                    issueInBoardEpicScope(i, issueById, selectedEpicId),
                                )
                              : [];
                          const storyIdx =
                            issue.issuetype === 'Story' &&
                            reorderInOpenColumn &&
                            issueInBoardEpicScope(issue, issueById, selectedEpicId)
                              ? epicStoriesInCol.findIndex((s) => String(s.issueid) === String(issue.issueid))
                              : -1;
                          const storyReorder =
                            reorderInOpenColumn &&
                            isAdmin &&
                            issue.issuetype === 'Story' &&
                            issueInBoardEpicScope(issue, issueById, selectedEpicId) &&
                            storyIdx >= 0
                              ? {
                                  first: storyIdx <= 0,
                                  onUp: () => reorderEpicStoriesInColumn(col, issue.issueid, 'up'),
                                }
                              : null;
                          const parentKey =
                            issue.issuetype === 'Task' &&
                            issue.parent_id != null &&
                            issue.parent_id !== ''
                              ? String(issue.parent_id)
                              : '';
                          const siblingTasksInCol =
                            isAdmin && reorderInOpenColumn && parentKey && issue.issuetype === 'Task'
                              ? issues.filter(
                                  (i) =>
                                    i.issuetype === 'Task' &&
                                    i.parent_id != null &&
                                    i.parent_id !== '' &&
                                    String(i.parent_id) === parentKey &&
                                    issueInBoardEpicScope(i, issueById, selectedEpicId),
                                )
                              : [];
                          const taskIdx =
                            issue.issuetype === 'Task' &&
                            reorderInOpenColumn &&
                            parentKey &&
                            issueInBoardEpicScope(issue, issueById, selectedEpicId)
                              ? siblingTasksInCol.findIndex((t) => String(t.issueid) === String(issue.issueid))
                              : -1;
                          const taskReorder =
                            reorderInOpenColumn &&
                            isAdmin &&
                            issue.issuetype === 'Task' &&
                            issueInBoardEpicScope(issue, issueById, selectedEpicId) &&
                            parentKey &&
                            taskIdx >= 0
                              ? {
                                  first: taskIdx <= 0,
                                  onUp: () => reorderEpicTasksInColumn(col, issue.issueid, 'up'),
                                }
                              : null;
                          return (
                        <IssueCard
                          key={issue.issueid}
                          issue={issue}
                          canEdit={true}
                          canDelete={canDeleteItems}
                          onDeleteStory={(issueId) => deleteStoryMutation.mutate(issueId)}
                          isInBacklog={false}
                          allIssues={boardData?.allIssues || []}
                          users={userListForAssign}
                          baseAssignableUsers={boardBaseAssignableUsers}
                          teamsMeta={boardTeamsMeta}
                          onStatusChange={(id, status) => statusMutation.mutate({ issueId: id, status })}
                          onReassign={(id, assigneeId) => reassignMutation.mutate({ issueId: id, assigneeId })}
                          onOpenTaskDetail={(task) => setSelectedDirectTask(task)}
                          onOpenTaskModal={(story, tasks) => setTaskModal({ story, tasks })}
                          storyReorder={storyReorder}
                          taskReorder={taskReorder}
                          boardDepartment={boardDepartment}
                        />
                          );
                        })
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      </>

      {/* ── Modals ── */}

      {epicDetailModalId && epicModalIssue && (
        <EpicDetailModal
          epic={epicModalIssue}
          activeSprint={activeSprint}
          projects={projects}
          eligibleSprints={epicMoveSprints}
          currentSprintId={selectedSprint}
          sprintDisplayName={epicModalSprintDisplay}
          canEdit={canChangeEpicStatus}
          onClose={() => setEpicDetailModalId(null)}
          onSaved={() => {
            invalidateBoard();
            invalidateSprints();
          }}
          onMoveEpicToBacklog={(issueId) => {
            moveMutation.mutate({ issueId, sprintId: null }, {
              onSettled: () => {
                setEpicDetailModalId(null);
                setSelectedEpicId(null);
              },
            });
          }}
          onMoveEpicToSprint={(issueId, sprintId) => {
            moveMutation.mutate({ issueId, sprintId }, {
              onSettled: () => setEpicDetailModalId(null),
            });
          }}
          onDeleteEpic={(issueId, title) => {
            if (!window.confirm(`Delete epic "${title}"?\n\nThis will permanently delete the epic. Stories and tasks linked to it will remain but lose their epic association.`)) return;
            deleteEpicMutation.mutate(issueId);
          }}
          onNavigateToAI={navigateToAiPlanning}
        />
      )}

      {selectedDirectTask && (
        <TaskDetailModal
          task={selectedDirectTask}
          onClose={() => {
            setSelectedDirectTask(null);
            setStoryDetailReturn(null);
          }}
          onBackToParent={
            storyDetailReturn
              ? () => {
                  const storyId = storyDetailReturn.issueid;
                  const fresh = (boardData?.allIssues || []).find(
                    (i) => String(i.issueid) === String(storyId),
                  );
                  setSelectedDirectTask(fresh || storyDetailReturn);
                  setStoryDetailReturn(null);
                }
              : undefined
          }
          onStatusChange={(id, status) => statusMutation.mutate({ issueId: id, status })}
          onReassign={(id, assigneeId) => reassignMutation.mutate({ issueId: id, assigneeId })}
          users={users.data || []}
          childTaskCount={
            selectedDirectTask.issuetype === 'Story'
              ? (boardData?.allIssues || []).filter(i => String(i.parent_id) === String(selectedDirectTask.issueid)).length
              : 0
          }
          childTasks={
            selectedDirectTask.issuetype === 'Story'
              ? (boardData?.allIssues || []).filter(
                  (i) =>
                    i.issuetype === 'Task' && String(i.parent_id) === String(selectedDirectTask.issueid),
                )
              : []
          }
          onOpenChildTask={(child) => {
            if (selectedDirectTask?.issuetype === 'Story') {
              setStoryDetailReturn(selectedDirectTask);
            }
            setSelectedDirectTask(child);
          }}
          projectId={effectiveProjectId || selectedDirectTask.projectid || selectedDirectTask.project_id || null}
          sprintId={selectedSprint || selectedDirectTask.sprint_id || null}
          onTaskCreated={() => invalidateBoard()}
          onMoveToSprint={
            isAdmin && selectedDirectTask?.issuetype === 'Story'
              ? (id, sprintId) => moveMutation.mutate({ issueId: id, sprintId })
              : null
          }
          moveTargetSprints={moveTargetSprints}
          onMoveToStory={(taskId, storyId) =>
            api.put(`/issues/${taskId}`, { parent_id: storyId }).then(() => invalidateBoard())}
          availableStories={filterEpicsForSprintPicker(
            (boardData?.allIssues || []).filter(
              (i) => i.issuetype === 'Story' && String(i.issueid) !== String(selectedDirectTask.issueid),
            ),
            selectedDirectTask.sprint_id,
          )}
          parentIssue={(boardData?.allIssues || []).find((i) => String(i.issueid) === String(selectedDirectTask.parent_id))}
          canEdit
          canDelete={canDeleteItems}
          onDeleteIssue={
            canDeleteItems
              ? (issueId) =>
                  deleteIssue(issueId).then(() => {
                    invalidateBoard();
                    setSelectedDirectTask(null);
                  })
              : undefined
          }
        />
      )}

      {taskModal && storyForKanbanModal && (
        <TaskKanbanModal
          story={storyForKanbanModal}
          tasks={boardData?.allIssues?.filter((i) => String(i.parent_id) === String(storyForKanbanModal.issueid)) || taskModal.tasks}
          users={users.data || []}
          sprintId={selectedSprint || null}
          projectId={effectiveProjectId || null}
          onClose={() => setTaskModal(null)}
          onStatusChange={(id, status) => statusMutation.mutate({ issueId: id, status })}
          onReassign={(id, assigneeId) => reassignMutation.mutate({ issueId: id, assigneeId })}
          onTaskCreated={() => { invalidateBoard(); }}
          onStoryUpdated={() => { invalidateBoard(); }}
          onDeleteStory={canDeleteItems ? (id) => deleteStoryMutation.mutate(id) : null}
          onMoveToBacklog={isAdmin ? (id) => moveMutation.mutate({ issueId: id, sprintId: null }) : null}
          canDelete={canDeleteItems}
          allBoardIssues={boardData?.allIssues || []}
          onMoveTaskToStory={(taskId, storyId) =>
            api.put(`/issues/${taskId}`, { parent_id: storyId }).then(() => invalidateBoard())
          }
          canEditTaskDetail
          canEditStoryEpic={isAdmin}
          onDeleteTask={
            canDeleteItems
              ? (issueId) =>
                  deleteIssue(issueId).then(() => {
                    invalidateBoard();
                  })
              : null
          }
          boardDepartment={boardDepartment}
        />
      )}

      {showNewProject && (
        <NewProjectModal
          onClose={() => setShowNewProject(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['projects'] })}
          department={effectiveBoardDepartment}
        />
      )}

      {(showSprintForm || editingSprint) && (
        <SprintFormModal
          projects={projects}
          existingSprint={editingSprint}
          onClose={() => { setShowSprintForm(false); setEditingSprint(null); }}
          onSaved={(sprintId, meta) => {
            invalidateSprints();
            qc.invalidateQueries({ queryKey: ['board'] });
            if (meta?.isNew && sprintId != null && sprintId !== '' && meta.projectId != null && meta.projectId !== '') {
              setSelectedProject(String(meta.projectId));
              setSelectedSprint(String(sprintId));
            }
            setShowSprintForm(false);
            setEditingSprint(null);
          }}
          onNavigateToAI={navigateToAiPlanning}
          onDelete={(sprintId, deleteIssues) => {
            deleteSprintMutation.mutate({ id: sprintId, deleteIssues });
            setEditingSprint(null);
          }}
          department={boardDepartment}
        />
      )}

      {showRelease && (() => {
        const trimmedVersion = String(releaseData.version || '').trim();
        const versionMissing = trimmedVersion.length === 0;
        const releaseDisabled = releaseMutation.isPending || versionMissing;
        return (
          <div style={modalOverlay}>
            <div style={modalBox} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, color: C.ink, fontWeight: 800 }}>🚀 Release Sprint</h3>
                <button onClick={closeReleaseDialog} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
              </div>
              <label style={labelStyle}>Version Tag *</label>
              <input
                style={{
                  ...inputStyle,
                  borderColor: versionMissing ? C.danger : inputStyle.border ? undefined : C.line,
                }}
                value={releaseData.version}
                onChange={e => setReleaseData(d => ({ ...d, version: e.target.value }))}
                placeholder="e.g. v1.3.0"
                required
              />
              <p style={{
                margin: '6px 0 0',
                fontSize: '0.72rem',
                color: versionMissing ? C.danger : C.inkSoft,
                lineHeight: 1.4,
              }}>
                {versionMissing
                  ? 'Required — releases without a version show as “Untagged” in Release History.'
                  : 'Used as the release tag everywhere this sprint surfaces (Releases page, project notes).'}
              </p>
              <label style={labelStyle}>Release Notes</label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={4}
                value={releaseData.release_notes}
                onChange={e => setReleaseData(d => ({ ...d, release_notes: e.target.value }))}
                placeholder="What was completed in this sprint…" />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={closeReleaseDialog} style={btnStyle(C.inkFaint)}>Cancel</button>
                <button
                  onClick={() => {
                    if (versionMissing) {
                      toast.error('Add a version tag before releasing the sprint.');
                      return;
                    }
                    releaseMutation.mutate({ ...releaseData, version: trimmedVersion });
                  }}
                  disabled={releaseDisabled}
                  style={{
                    ...btnStyle(C.done),
                    opacity: releaseDisabled ? 0.55 : 1,
                    cursor: releaseDisabled ? 'not-allowed' : 'pointer',
                  }}
                  title={versionMissing ? 'Add a version tag first' : undefined}
                >
                  {releaseMutation.isPending ? 'Releasing…' : '🚀 Release'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showIssueForm && (
        <WorkItemCreateModal
          open={showIssueForm}
          onClose={() => setShowIssueForm(false)}
          sprintId={selectedSprint || null}
          projectId={effectiveProjectId || null}
          sprintList={sprints}
          allowSprintChange
          sprintsEnabled={usesSprints}
          defaultEpicId={selectedEpicId || null}
          onCreated={() => {
            invalidateBoard();
            qc.invalidateQueries({ queryKey: ['backlog', selectedProject] });
          }}
        />
      )}

      {/* Add to Backlog Modal */}
      {showBacklogForm && (
        <div style={modalOverlay}>
          <div style={issueFormModalPanelStyle} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: C.ink, fontWeight: 800 }}>📋 Add to Backlog</h3>
              <button onClick={() => setShowBacklogForm(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
            </div>
            <div style={{ background: C.warnSoft, border: `1px solid ${C.warnSoft}`, borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: '0.82rem', color: C.warn }}>
              Add a <strong>Story</strong> to the backlog (no sprint). Tasks are created under stories on the sprint board.
            </div>
            <IssueForm 
              sprintId={null}
              projectId={selectedProject || null}
              sprintList={[]}
              allowSprintChange={false}
              sprintsEnabled={usesSprints}
              fixedType="Story"
              autoGrowDescription
              onSuccess={() => { 
                qc.invalidateQueries({ queryKey: ['backlog', selectedProject] });
                setShowBacklogForm(false); 
                toast.success('Story added to backlog'); 
              }} 
            />
          </div>
        </div>
      )}

      {/* Confirmation Dialog Modal */}
      {confirmDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '1rem',
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '1.5rem 1.75rem',
            width: '100%', maxWidth: 400, boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              fontSize: '1.1rem', fontWeight: 800, color: C.ink, marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {confirmDialog.title}
            </div>
            <div style={{
              fontSize: '0.9rem', fontWeight: 600, color: confirmDialog.warning ? C.warn : C.ink,
              marginBottom: 8,
            }}>
              {confirmDialog.message}
            </div>
            {confirmDialog.details && (
              <div style={{
                fontSize: '0.82rem', color: C.inkSoft, lineHeight: 1.5,
                background: confirmDialog.warning ? C.warnSoft : C.sunken,
                border: `1px solid ${confirmDialog.warning ? C.warnSoft : C.line}`,
                borderRadius: 8, padding: '10px 12px', marginBottom: 16,
                whiteSpace: 'pre-line',
              }}>
                {confirmDialog.details}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() => setConfirmDialog(null)}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.line}`,
                  background: C.sunken, color: C.inkSoft, fontWeight: 600,
                  fontSize: '0.85rem', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: confirmDialog.confirmColor || C.accentInk, color: '#fff',
                  fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                }}
              >
                {confirmDialog.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DailyNotebookModal
        open={showNotebookModal && !!effectiveProjectId}
        onClose={() => setShowNotebookModal(false)}
        projectId={effectiveProjectId}
        sprintId={selectedSprint}
        projectName={
          orderedProjects.find((p) => String(p.projectid) === String(effectiveProjectId))?.projectname
          || ''
        }
        sprintName={activeSprint?.sprint_name || ''}
        canEdit={Boolean(effectiveProjectId && isSelectedProjectInScope)}
        canManageTasks={isAdmin}
        canDelete={canDeleteItems}
        department={workflowDepartment}
        sprintClosed={['Released', 'Closed'].includes(activeSprint?.status)}
      />
    </div>
  );
};

export default SprintBoard;
